import React, { useState, useEffect } from 'react';
import Papa from 'papaparse';
import './EnergyCalculator.css';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { format, parseISO, startOfDay, endOfDay, eachDayOfInterval } from 'date-fns';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler
);

// Hardcoded sample data
const sampleData = [
  { 'Date/Time': '7/6/19 23:00', 'DALMP Average': 20.1 },
  { 'Date/Time': '7/7/19 0:00', 'DALMP Average': 18.3 },
  { 'Date/Time': '7/7/19 1:00', 'DALMP Average': 16.5 },
  { 'Date/Time': '7/7/19 2:00', 'DALMP Average': 15.6 },
  { 'Date/Time': '7/7/19 3:00', 'DALMP Average': 15.4 },
  { 'Date/Time': '7/7/19 4:00', 'DALMP Average': 15.2 },
  { 'Date/Time': '7/7/19 5:00', 'DALMP Average': 15.1 },
  { 'Date/Time': '7/7/19 6:00', 'DALMP Average': 15.1 },
  { 'Date/Time': '7/7/19 7:00', 'DALMP Average': 15.1 },
  { 'Date/Time': '7/7/19 8:00', 'DALMP Average': 15.7 },
  { 'Date/Time': '7/7/19 9:00', 'DALMP Average': 17.6 },
  { 'Date/Time': '7/7/19 10:00', 'DALMP Average': 19.5 },
  { 'Date/Time': '7/7/19 11:00', 'DALMP Average': 21.9 },
  { 'Date/Time': '7/7/19 12:00', 'DALMP Average': 27.6 },
  { 'Date/Time': '7/7/19 13:00', 'DALMP Average': 28.2 },
  { 'Date/Time': '7/7/19 14:00', 'DALMP Average': 26.6 },
  { 'Date/Time': '7/7/19 15:00', 'DALMP Average': 27.9 },
  { 'Date/Time': '7/7/19 16:00', 'DALMP Average': 30.3 },
  { 'Date/Time': '7/7/19 17:00', 'DALMP Average': 34.1 },
  { 'Date/Time': '7/7/19 18:00', 'DALMP Average': 30.9 },
  { 'Date/Time': '7/7/19 19:00', 'DALMP Average': 26.9 },
  { 'Date/Time': '7/7/19 20:00', 'DALMP Average': 24.3 },
  { 'Date/Time': '7/7/19 21:00', 'DALMP Average': 20.8 },
  { 'Date/Time': '7/7/19 22:00', 'DALMP Average': 19.6 },
  { 'Date/Time': '7/7/19 23:00', 'DALMP Average': 18.1 },
  { 'Date/Time': '7/8/19 0:00', 'DALMP Average': 16.6 },
  { 'Date/Time': '7/8/19 1:00', 'DALMP Average': 15.5 },
  { 'Date/Time': '7/8/19 2:00', 'DALMP Average': 14.5 },
  { 'Date/Time': '7/8/19 3:00', 'DALMP Average': 13.9 },
  { 'Date/Time': '7/8/19 4:00', 'DALMP Average': 13.8 },
  { 'Date/Time': '7/8/19 5:00', 'DALMP Average': 13.8 },
  { 'Date/Time': '7/8/19 6:00', 'DALMP Average': 14.4 },
  { 'Date/Time': '7/8/19 7:00', 'DALMP Average': 16.0 },
  { 'Date/Time': '7/8/19 8:00', 'DALMP Average': 17.7 },
  { 'Date/Time': '7/8/19 9:00', 'DALMP Average': 19.6 },
  { 'Date/Time': '7/8/19 10:00', 'DALMP Average': 21.9 },
  { 'Date/Time': '7/8/19 11:00', 'DALMP Average': 26.3 },
  { 'Date/Time': '7/8/19 12:00', 'DALMP Average': 31.7 },
  { 'Date/Time': '7/8/19 13:00', 'DALMP Average': 31.1 },
  { 'Date/Time': '7/8/19 14:00', 'DALMP Average': 28.7 },
  { 'Date/Time': '7/8/19 15:00', 'DALMP Average': 28.4 },
  { 'Date/Time': '7/8/19 16:00', 'DALMP Average': 31.1 },
  { 'Date/Time': '7/8/19 17:00', 'DALMP Average': 32.0 },
  { 'Date/Time': '7/8/19 18:00', 'DALMP Average': 30.3 },
  { 'Date/Time': '7/8/19 19:00', 'DALMP Average': 24.9 },
  { 'Date/Time': '7/8/19 20:00', 'DALMP Average': 23.1 },
  { 'Date/Time': '7/8/19 21:00', 'DALMP Average': 20.8 }
];

function EnergyCalculator() {
  console.log('EnergyCalculator component loading...');

  // Disable theme CSS and inject global reset on mount, restore on unmount
  useEffect(() => {
    // Find and disable all theme stylesheets
    const themeStylesheets = document.querySelectorAll('link[rel="stylesheet"]');
    const disabledSheets = [];

    themeStylesheets.forEach(sheet => {
      const href = sheet.getAttribute('href');
      // Disable all theme CSS (everything in /css/, /rs-plugin/)
      if (href && (href.includes('/css/') || href.includes('/rs-plugin/'))) {
        sheet.disabled = true;
        disabledSheets.push(sheet);
      }
    });

    // Save original styles for restoration
    const originalBodyStyle = document.body.style.cssText;
    const originalHtmlStyle = document.documentElement.style.cssText;

    // Inject global reset (matching DF_App's index.css)
    const resetStyle = document.createElement('style');
    resetStyle.id = 'energy-calculator-reset';
    resetStyle.textContent = `
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
        min-height: 100vh;
        color: #f1f5f9;
      }
    `;
    document.head.appendChild(resetStyle);

    // Remove Bootstrap data attributes
    document.body.removeAttribute('data-bs-spy');
    document.body.removeAttribute('data-bs-target');
    document.body.removeAttribute('data-bs-offset');

    // Cleanup: restore everything when leaving calculator
    return () => {
      disabledSheets.forEach(sheet => {
        sheet.disabled = false;
      });
      document.body.style.cssText = originalBodyStyle;
      document.documentElement.style.cssText = originalHtmlStyle;
      const injectedReset = document.getElementById('energy-calculator-reset');
      if (injectedReset) {
        injectedReset.remove();
      }
    };
  }, []);

  // Month abbreviations for display
  const monthAbbreviations = ['', 'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  
  const [data, setData] = useState(null);
  const [rawData, setRawData] = useState(sampleData); // Store raw data separately
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('daily'); // Daily Performance is the main view
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [selectedDate, setSelectedDate] = useState(null);
  const [curtailmentThreshold, setCurtailmentThreshold] = useState(22); // $22/MWh target
  const [gridSaleThreshold, setGridSaleThreshold] = useState(75); // $75/MWh triggers curtail-for-sale
  const [monthWindow, setMonthWindow] = useState(0); // 0 = months 0-60, 12 = months 12-72, etc.
  const [tempMonthWindow, setTempMonthWindow] = useState(0); // Temporary value while dragging
  const [showConditionsInfo, setShowConditionsInfo] = useState(false); // Toggle for economics info
  const [revenueSplit, setRevenueSplit] = useState('50/50'); // CU/Operator revenue split
  const [showModelingCalcs, setShowModelingCalcs] = useState(false); // Toggle for modeling calculations section
  const [useAllData, setUseAllData] = useState(false); // Toggle to use all 10 years vs window
  const [dataStats, setDataStats] = useState(null); // Store calculated statistics
  
  // CPER calculation inputs - Variable Operating Costs
  const [heatRate, setHeatRate] = useState(15); // MMBtu/MWh - typical for subcritical coal (range 9.5-11.5)
  const [coalPrice, setCoalPrice] = useState(1.75); // $/MMBtu - PRB delivered price (range $1.00-3.00)
  const [variableOM, setVariableOM] = useState(3); // $/MWh - chemicals, consumables, variable labor
  
  // Fixed Operating Costs (default 0 - user can add if pilot should share)
  const [plantStaffLabor, setPlantStaffLabor] = useState(0); // $/MWh - fixed staff costs allocated
  const [maintenanceContracts, setMaintenanceContracts] = useState(0); // $/MWh - annual maintenance contracts
  
  // Capital & Capacity (default 0 - user can add if needed)
  const [capacityRecovery, setCapacityRecovery] = useState(0); // $/MWh - debt service, depreciation, ROI
  
  // Environmental & Grid (default 0 - user can add if needed)
  const [environmentalCompliance, setEnvironmentalCompliance] = useState(0); // $/MWh - emissions, ash, controls
  const [gridAdminCosts, setGridAdminCosts] = useState(0); // $/MWh - SPP charges, NERC, overhead
  
  // Contract Structure
  const [marginAboveCost, setMarginAboveCost] = useState(0); // $/MWh - optional margin or risk premium
  
  // CU Cost Avoidances - Cold Start inputs
  const [savingsPerColdStart, setSavingsPerColdStart] = useState(80000); // $ per cold start avoided
  const [coldStartsAvoidedYearly, setColdStartsAvoidedYearly] = useState(12); // number of cold starts avoided per year
  
  // CU Cost Avoidances - Wear and Tear
  const [wearTearSavings5Year, setWearTearSavings5Year] = useState(2500000); // $ total savings over 5 years
  
  // CU Cost Avoidances - Heat Rate Improvement inputs
  const [baseHeatRate, setBaseHeatRate] = useState(10500); // BTU/kWh base heat rate for cycling operation
  const [improvedHeatRate, setImprovedHeatRate] = useState(10000); // BTU/kWh improved heat rate with steady operation
  
  // Uptime configuration - single source of truth
  const opUptimeTarget = 0.90; // 90% uptime target for operator payment
  const plantCapacityMW = 50; // MW
  const hoursPerYear = 8760;
  const [annualGenerationMWh, setAnnualGenerationMWh] = useState(plantCapacityMW * opUptimeTarget * hoursPerYear); // MWh/year
  
  // Calculate derived avoidance values
  const coldStartAvoidance = savingsPerColdStart * coldStartsAvoidedYearly; // $/year
  const cyclingWearSavings = wearTearSavings5Year / 5; // $/year average
  const heatRateImprovementPercent = ((baseHeatRate - improvedHeatRate) / baseHeatRate) * 100;
  const heatRateImprovements = (heatRateImprovementPercent / 100) * annualGenerationMWh * heatRate * coalPrice; // $/year from efficiency gain
  
  // Calculate the CPER based on all inputs
  const calculatedCPER = Math.max(0, (
    heatRate * coalPrice + // Fuel cost
    variableOM + // Variable O&M
    plantStaffLabor + // Fixed staff
    maintenanceContracts + // Fixed maintenance
    capacityRecovery + // Capital recovery
    environmentalCompliance + // Environmental costs
    gridAdminCosts - // Grid/admin costs
    (coldStartAvoidance + cyclingWearSavings + heatRateImprovements) / annualGenerationMWh + // Minus cost avoidances per MWh
    marginAboveCost // Plus margin
  ));
  
  // Update curtailmentThreshold to match calculated CPER for all downstream calculations
  useEffect(() => {
    setCurtailmentThreshold(calculatedCPER);
  }, [calculatedCPER]);

  // Auto-load TSV data file on component mount if available
  useEffect(() => {
    const loadDataFile = async () => {
      try {
        setLoading(true);
        const response = await fetch('/data/dataset.tsv');
        
        if (response.ok) {
          const text = await response.text();
          console.log('Auto-loading dataset.tsv...');
          
          // Parse TSV data
          Papa.parse(text, {
            header: true,
            delimiter: '\t',
            complete: (results) => {
              if (results.data && results.data.length > 0) {
                // Process the data
                const processedData = results.data.map(row => ({
                  'Date/Time': row['Date/Time'] || row['DateTime'] || row['Date'] || '',
                  'DALMP Average': parseFloat(row['DALMP Average'] || row['DALMP'] || row['Price'] || 0)
                })).filter(row => row['Date/Time'] && !isNaN(row['DALMP Average']));
                
                if (processedData.length > 0) {
                  console.log(`Successfully loaded ${processedData.length} rows from dataset.tsv`);
                  setRawData(processedData);
                  setData(processedData);
                } else {
                  console.log('No valid data found in dataset.tsv, using default sample data');
                }
              }
            },
            error: (error) => {
              console.error('Error parsing TSV file:', error);
              console.log('Using default sample data');
            }
          });
        } else {
          console.log('dataset.tsv not found, using default sample data');
        }
      } catch (error) {
        console.log('Could not load dataset.tsv, using default sample data');
      } finally {
        setLoading(false);
      }
    };

    // Load data file on mount
    loadDataFile();
  }, []);

  // Calculate comprehensive statistics for the modeling section
  const calculateDataStats = (dataToAnalyze) => {
    console.log('calculateDataStats called with:', dataToAnalyze?.length, 'days');
    
    if (!dataToAnalyze || dataToAnalyze.length === 0) {
      console.log('No data to analyze');
      return null;
    }
    
    // Try to get prices from the data
    let prices = [];
    
    // Check if this is the raw sample data format
    if (dataToAnalyze[0]?.hours && Array.isArray(dataToAnalyze[0].hours)) {
      console.log('Found hours array in first day');
      prices = dataToAnalyze.flatMap(day => 
        day.hours ? day.hours.map(h => h.price || 0) : []
      );
    } 
    // Check if it's the processed data format
    else if (dataToAnalyze[0]?.average !== undefined) {
      console.log('Found average in first day - using daily averages');
      prices = dataToAnalyze.map(day => day.average);
    }
    // Try to extract from any numeric fields
    else {
      console.log('Unknown format, trying to find prices');
      console.log('First item structure:', JSON.stringify(dataToAnalyze[0], null, 2).slice(0, 500));
    }
    
    console.log('Extracted', prices.length, 'prices');
    
    if (prices.length === 0) {
      console.log('No prices found - returning dummy data for testing');
      // Return dummy data to test if display works
      return {
        metadata: {
          startDate: '2014-01-01',
          endDate: '2024-01-01',
          totalHours: 1000,
          totalDays: 42
        },
        basic: {
          mean: '25.50',
          median: '24.00',
          mode: '22',
          min: '-5.00',
          max: '150.00',
          stdDev: '15.25',
          variance: '232.56',
          skewness: '1.234',
          kurtosis: '2.345'
        },
        percentiles: {
          p1: '2.00',
          p5: '8.00',
          p10: '12.00',
          p25: '18.00',
          p50: '24.00',
          p75: '35.00',
          p90: '55.00',
          p95: '75.00',
          p99: '120.00'
        },
        priceBands: {
          negative: 50,
          band_0_10: 200,
          band_10_20: 500,
          band_20_30: 800,
          band_30_40: 400,
          band_40_50: 200,
          band_50_75: 150,
          band_75_100: 75,
          band_100_200: 25,
          band_200_plus: 5
        },
        hoursBelow: {
          below_0: 50,
          below_10: 250,
          below_22: 600,
          below_30: 1400,
          below_40: 1800,
          below_50: 2000,
          below_65: 2200,
          below_75: 2275,
          below_100: 2350
        },
        capacityFactors: {
          cf_20: '75.0',
          cf_22: '73.0',
          cf_30: '65.0',
          cf_40: '55.0',
          cf_50: '45.0',
          cf_75: '25.0',
          cf_100: '10.0'
        },
        hourlyAverages: Array(24).fill('25.00'),
        dayOfWeekAverages: {
          sunday: '22.00',
          monday: '26.00',
          tuesday: '27.00',
          wednesday: '28.00',
          thursday: '27.00',
          friday: '26.00',
          saturday: '23.00'
        },
        monthlyAverages: Array(12).fill('25.00'),
        volatility: {
          avgHourlyChange: '5.50',
          maxHourlyIncrease: '85.00',
          maxHourlyDecrease: '-45.00'
        }
      };
    }
    
    // Sort for percentile calculations
    const sortedPrices = [...prices].sort((a, b) => a - b);
    
    // Basic statistics
    const mean = prices.reduce((a, b) => a + b, 0) / prices.length;
    const median = sortedPrices[Math.floor(sortedPrices.length / 2)];
    const min = sortedPrices[0];
    const max = sortedPrices[sortedPrices.length - 1];
    
    // Mode (most frequent price - binned to nearest dollar)
    const priceBins = {};
    prices.forEach(p => {
      const bin = Math.round(p);
      priceBins[bin] = (priceBins[bin] || 0) + 1;
    });
    const mode = Object.entries(priceBins).sort((a, b) => b[1] - a[1])[0][0];
    
    // Standard deviation and variance
    const variance = prices.reduce((acc, val) => acc + Math.pow(val - mean, 2), 0) / prices.length;
    const stdDev = Math.sqrt(variance);
    
    // Skewness and kurtosis
    const n = prices.length;
    const m3 = prices.reduce((acc, val) => acc + Math.pow(val - mean, 3), 0) / n;
    const m4 = prices.reduce((acc, val) => acc + Math.pow(val - mean, 4), 0) / n;
    const skewness = m3 / Math.pow(stdDev, 3);
    const kurtosis = (m4 / Math.pow(stdDev, 4)) - 3;
    
    // Percentiles
    const getPercentile = (p) => sortedPrices[Math.floor((sortedPrices.length - 1) * p / 100)];
    
    // Price bands (hours in each band)
    const priceBands = {
      negative: prices.filter(p => p < 0).length,
      band_0_10: prices.filter(p => p >= 0 && p < 10).length,
      band_10_20: prices.filter(p => p >= 10 && p < 20).length,
      band_20_30: prices.filter(p => p >= 20 && p < 30).length,
      band_30_40: prices.filter(p => p >= 30 && p < 40).length,
      band_40_50: prices.filter(p => p >= 40 && p < 50).length,
      band_50_75: prices.filter(p => p >= 50 && p < 75).length,
      band_75_100: prices.filter(p => p >= 75 && p < 100).length,
      band_100_200: prices.filter(p => p >= 100 && p < 200).length,
      band_200_plus: prices.filter(p => p >= 200).length
    };
    
    // Threshold analysis
    const thresholds = [0, 10, 22, 30, 40, 50, 65, 75, 100];
    const hoursBelow = {};
    thresholds.forEach(t => {
      hoursBelow[`below_${t}`] = prices.filter(p => p < t).length;
    });
    
    // Capacity factors
    const totalHours = prices.length;
    const capacityFactors = {};
    [20, 22, 30, 40, 50, 75, 100].forEach(threshold => {
      const hoursAbove = prices.filter(p => p >= threshold).length;
      capacityFactors[`cf_${threshold}`] = ((hoursAbove / totalHours) * 100).toFixed(1);
    });
    
    // Temporal patterns - hour of day
    const hourlyStats = Array(24).fill(null).map(() => []);
    dataToAnalyze.forEach(day => {
      if (day.hours) {
        day.hours.forEach((hour, idx) => {
          if (idx < 24) hourlyStats[idx].push(hour.price);
        });
      }
    });
    
    const hourlyAverages = hourlyStats.map(hours => 
      hours.length > 0 ? (hours.reduce((a, b) => a + b, 0) / hours.length).toFixed(2) : 0
    );
    
    // Day of week patterns (0 = Sunday)
    const dayOfWeekStats = Array(7).fill(null).map(() => []);
    dataToAnalyze.forEach(day => {
      const date = new Date(day.date);
      const dow = date.getDay();
      const dayAvg = day.hours ? day.hours.reduce((sum, h) => sum + h.price, 0) / day.hours.length : 0;
      dayOfWeekStats[dow].push(dayAvg);
    });
    
    const dayOfWeekAverages = {
      sunday: dayOfWeekStats[0].length > 0 ? (dayOfWeekStats[0].reduce((a, b) => a + b, 0) / dayOfWeekStats[0].length).toFixed(2) : 0,
      monday: dayOfWeekStats[1].length > 0 ? (dayOfWeekStats[1].reduce((a, b) => a + b, 0) / dayOfWeekStats[1].length).toFixed(2) : 0,
      tuesday: dayOfWeekStats[2].length > 0 ? (dayOfWeekStats[2].reduce((a, b) => a + b, 0) / dayOfWeekStats[2].length).toFixed(2) : 0,
      wednesday: dayOfWeekStats[3].length > 0 ? (dayOfWeekStats[3].reduce((a, b) => a + b, 0) / dayOfWeekStats[3].length).toFixed(2) : 0,
      thursday: dayOfWeekStats[4].length > 0 ? (dayOfWeekStats[4].reduce((a, b) => a + b, 0) / dayOfWeekStats[4].length).toFixed(2) : 0,
      friday: dayOfWeekStats[5].length > 0 ? (dayOfWeekStats[5].reduce((a, b) => a + b, 0) / dayOfWeekStats[5].length).toFixed(2) : 0,
      saturday: dayOfWeekStats[6].length > 0 ? (dayOfWeekStats[6].reduce((a, b) => a + b, 0) / dayOfWeekStats[6].length).toFixed(2) : 0,
    };
    
    // Monthly patterns
    const monthlyStats = Array(12).fill(null).map(() => []);
    dataToAnalyze.forEach(day => {
      const date = new Date(day.date);
      const month = date.getMonth();
      const dayAvg = day.hours ? day.hours.reduce((sum, h) => sum + h.price, 0) / day.hours.length : 0;
      monthlyStats[month].push(dayAvg);
    });
    
    const monthlyAverages = monthlyStats.map((months, idx) => 
      months.length > 0 ? (months.reduce((a, b) => a + b, 0) / months.length).toFixed(2) : 0
    );
    
    // Volatility metrics
    const hourlyChanges = [];
    for (let i = 1; i < prices.length; i++) {
      hourlyChanges.push(Math.abs(prices[i] - prices[i-1]));
    }
    const avgHourlyChange = hourlyChanges.length > 0 ? 
      (hourlyChanges.reduce((a, b) => a + b, 0) / hourlyChanges.length).toFixed(2) : 0;
    const maxHourlyIncrease = Math.max(...hourlyChanges);
    const maxHourlyDecrease = Math.min(...hourlyChanges);
    
    // Date range
    const startDate = dataToAnalyze[0]?.date || '';
    const endDate = dataToAnalyze[dataToAnalyze.length - 1]?.date || '';
    
    return {
      metadata: {
        startDate,
        endDate,
        totalHours,
        totalDays: dataToAnalyze.length
      },
      basic: {
        mean: mean.toFixed(2),
        median: median.toFixed(2),
        mode: mode,
        min: min.toFixed(2),
        max: max.toFixed(2),
        stdDev: stdDev.toFixed(2),
        variance: variance.toFixed(2),
        skewness: skewness.toFixed(3),
        kurtosis: kurtosis.toFixed(3)
      },
      percentiles: {
        p1: getPercentile(1).toFixed(2),
        p5: getPercentile(5).toFixed(2),
        p10: getPercentile(10).toFixed(2),
        p25: getPercentile(25).toFixed(2),
        p50: median.toFixed(2),
        p75: getPercentile(75).toFixed(2),
        p90: getPercentile(90).toFixed(2),
        p95: getPercentile(95).toFixed(2),
        p99: getPercentile(99).toFixed(2)
      },
      priceBands,
      hoursBelow,
      capacityFactors,
      hourlyAverages,
      dayOfWeekAverages,
      monthlyAverages,
      volatility: {
        avgHourlyChange,
        maxHourlyIncrease: maxHourlyIncrease.toFixed(2),
        maxHourlyDecrease: maxHourlyDecrease.toFixed(2)
      }
    };
  };

  // Process data when thresholds or window changes (but not temp window)
  useEffect(() => {
    if (rawData && rawData.length > 0) {
      processData(rawData);
    }
  }, [curtailmentThreshold, gridSaleThreshold, monthWindow, rawData]);
  
  // Calculate stats when data or window changes
  useEffect(() => {
    console.log('Stats useEffect triggered. rawData length:', rawData?.length, 'useAllData:', useAllData, 'monthWindow:', monthWindow);
    
    if (rawData && rawData.length > 0) {
      let dataToAnalyze;
      if (useAllData) {
        // Use all raw data
        dataToAnalyze = rawData;
        console.log('Using all data:', dataToAnalyze.length, 'days');
      } else {
        // Use only the windowed portion of raw data
        const startIdx = monthWindow * 30; // Approximate days per month
        const endIdx = Math.min(startIdx + (60 * 30), rawData.length); // 60 months worth
        dataToAnalyze = rawData.slice(startIdx, endIdx);
        console.log('Using window data from index', startIdx, 'to', endIdx, '=', dataToAnalyze.length, 'days');
      }
      
      try {
        const stats = calculateDataStats(dataToAnalyze);
        console.log('Stats calculated:', stats ? 'Success' : 'Null returned');
        setDataStats(stats);
      } catch (error) {
        console.error('Error calculating stats:', error);
        setDataStats(null);
      }
    } else {
      console.log('No rawData available');
    }
  }, [rawData, useAllData, monthWindow]);
  
  // Initialize temp window
  useEffect(() => {
    setTempMonthWindow(monthWindow);
  }, [monthWindow]);

  // Process CSV/TSV data
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true);
    setError(null);

    // Detect delimiter based on file extension
    const delimiter = file.name.endsWith('.tsv') ? '\t' : ',';

    Papa.parse(file, {
      header: true,
      dynamicTyping: true,
      delimiter: delimiter,
      skipEmptyLines: true,
      complete: (results) => {
        try {
          setRawData(results.data); // Store the imported data
          processData(results.data);
          setLoading(false);
        } catch (err) {
          setError(`Error processing data: ${err.message}`);
          setLoading(false);
        }
      },
      error: (err) => {
        setError(`Error parsing file: ${err.message}`);
        setLoading(false);
      }
    });
  };

  // Process and anonymize data
  const processData = (rawData) => {
    // Filter out empty rows - check for Date/Time column or other date fields
    let allData = rawData.filter(row => 
      row['Date/Time'] || row.Date || row.date || row.DateTime
    );
    
    // Parse and sort all data
    const dataWithDates = allData.map(row => ({
      ...row,
      parsedDate: new Date(row['Date/Time'] || row.Date || row.date || row.DateTime)
    }));
    
    // Sort by date
    dataWithDates.sort((a, b) => a.parsedDate - b.parsedDate);
    
    // Keep reference to all data for charts
    const fullDataset = [...dataWithDates];
    
    // Filter to selected window for stats calculation only
    let filteredData = dataWithDates;
    if (dataWithDates.length > 100) {  // Only filter if we have substantial data
      const startDate = dataWithDates[0].parsedDate;
      const windowStartDate = new Date(startDate);
      windowStartDate.setMonth(windowStartDate.getMonth() + monthWindow);
      const windowEndDate = new Date(windowStartDate);
      windowEndDate.setMonth(windowEndDate.getMonth() + 60);
      
      // Filter for statistics
      filteredData = dataWithDates.filter(row => 
        row.parsedDate >= windowStartDate && row.parsedDate < windowEndDate
      );
    }
    
    // Anonymize location data if present
    const anonymizedData = filteredData.map((row, index) => ({
      ...row,
      location: `Location_${index % 5 + 1}`, // Group into 5 anonymized locations
      node: row.Node ? `Node_${row.Node.toString().slice(-3)}` : `Node_${index % 10}`,
    }));
    
    // Anonymize full dataset for charts
    const anonymizedFullData = fullDataset.map((row, index) => ({
      ...row,
      location: `Location_${index % 5 + 1}`, 
      node: row.Node ? `Node_${row.Node.toString().slice(-3)}` : `Node_${index % 10}`,
    }));

    // Calculate statistics on filtered window
    const stats = calculateStatistics(anonymizedData);
    
    setData({
      raw: anonymizedData,
      fullData: anonymizedFullData,  // Full dataset for charts
      stats,
      processed: processForCharts(anonymizedFullData),  // Process full data for charts
      windowProcessed: processForCharts(anonymizedData)  // Process window data for window indicator
    });

    // Set date range
    if (anonymizedData.length > 0) {
      const dates = anonymizedData.map(row => 
        new Date(row['Date/Time'] || row.Date || row.date || row.DateTime)
      ).filter(d => !isNaN(d));
      if (dates.length > 0) {
        setDateRange({
          start: new Date(Math.min(...dates)),
          end: new Date(Math.max(...dates))
        });
      }
    }
  };

  // Calculate datacenter operational statistics
  const calculateStatistics = (data) => {
    const dalmpValues = data.map(row => 
      row['DALMP Average'] || row.DALMP || row.dalmp || row.Price || 0
    ).filter(v => v > 0);
    
    if (dalmpValues.length === 0) return null;

    // Datacenter operation analysis - datacenter runs unless curtailed for grid sale or emergency
    // Assume 5% emergency curtailments randomly distributed
    const emergencyCurtailmentRate = 0.05;
    const gridSaleHours = dalmpValues.filter(v => v >= gridSaleThreshold).length;
    const totalHours = dalmpValues.length;
    const emergencyHours = Math.round(totalHours * emergencyCurtailmentRate);
    const datacenterHours = totalHours - gridSaleHours - emergencyHours;
    const normalOperatingHours = 0; // Not used in new logic
    const curtailedHours = gridSaleHours + emergencyHours; // Both types of curtailment
    const uptimePercent = ((datacenterHours + normalOperatingHours) / dalmpValues.length) * 100;
    
    // Calculate revenue opportunity from high-value curtailment only
    // Revenue = (Market Price - Contract Price) * 50MW * hours
    const curtailmentHourlyRevenues = dalmpValues
      .filter(v => v >= gridSaleThreshold)
      .map(price => (price - calculatedCPER) * 50); // 50MW datacenter capacity
    const totalCurtailmentRevenue = curtailmentHourlyRevenues.reduce((sum, rev) => sum + rev, 0);
    
    // Legacy calculation for backward compatibility
    const curtailmentRevenue = dalmpValues
      .filter(v => v >= gridSaleThreshold)
      .reduce((sum, price) => sum + (price - curtailmentThreshold), 0);
    
    // Average prices during different operational states
    const datacenterPrices = dalmpValues.filter(v => v <= curtailmentThreshold);
    const curtailedPrices = dalmpValues.filter(v => v > curtailmentThreshold);
    const avgDatacenterPrice = datacenterPrices.length > 0 ? 
      datacenterPrices.reduce((a, b) => a + b, 0) / datacenterPrices.length : 0;
    const avgCurtailedPrice = curtailedPrices.length > 0 ?
      curtailedPrices.reduce((a, b) => a + b, 0) / curtailedPrices.length : 0;

    const sum = dalmpValues.reduce((a, b) => a + b, 0);
    const avg = sum / dalmpValues.length;
    const sorted = [...dalmpValues].sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)];
    const min = Math.min(...dalmpValues);
    const max = Math.max(...dalmpValues);

    return {
      count: dalmpValues.length,
      average: avg,
      median,
      min,
      max,
      datacenterHours,
      normalOperatingHours,
      gridSaleHours,
      curtailedHours,
      uptimePercent,
      avgDatacenterPrice,
      avgCurtailedPrice,
      curtailmentRevenue,
      totalCurtailmentRevenue,
      curtailmentThreshold
    };
  };

  // Process data for datacenter operation charts
  const processForCharts = (data) => {
    // Group by hour for hourly analysis
    const hourlyData = {};
    const dailyData = {};
    
    data.forEach(row => {
      const dateStr = row['Date/Time'] || row.Date || row.date || row.DateTime;
      if (!dateStr) return;
      
      const date = new Date(dateStr);
      const hour = date.getHours();
      const day = format(date, 'yyyy-MM-dd');
      const dalmp = row['DALMP Average'] || row.DALMP || row.dalmp || row.Price || 0;
      
      // Hourly aggregation with datacenter status
      if (!hourlyData[hour]) {
        hourlyData[hour] = { prices: [], datacenter: [], curtailed: [] };
      }
      hourlyData[hour].prices.push(dalmp);
      // Datacenter runs unless grid sale price reached
      if (dalmp >= gridSaleThreshold) {
        hourlyData[hour].curtailed.push(dalmp);
      } else {
        hourlyData[hour].datacenter.push(dalmp);
      }
      
      // Daily aggregation with operational data
      if (!dailyData[day]) {
        dailyData[day] = { 
          prices: [], 
          hourlyData: [],
          datacenterHours: 0,
          normalOperatingHours: 0,
          gridSaleHours: 0,
          revenue: 0
        };
      }
      dailyData[day].prices.push(dalmp);
      
      // Datacenter runs continuously except for grid sales
      if (dalmp >= gridSaleThreshold) {
        dailyData[day].gridSaleHours++;
        dailyData[day].revenue += (dalmp - curtailmentThreshold);
        dailyData[day].hourlyData.push({ hour, price: dalmp, isDatacenterUp: false });
      } else {
        // Normal operation - datacenter operates regardless of market price
        dailyData[day].datacenterHours++;
        dailyData[day].hourlyData.push({ hour, price: dalmp, isDatacenterUp: true });
      }
    });

    // Calculate hourly operational patterns
    const hourlyAverages = Object.keys(hourlyData).map(hour => {
      const data = hourlyData[hour];
      const avgPrice = data.prices.reduce((a, b) => a + b, 0) / data.prices.length;
      const datacenterPercent = (data.datacenter.length / data.prices.length) * 100;
      
      return {
        hour: parseInt(hour),
        average: avgPrice,
        datacenterPercent,
        curtailmentPercent: 100 - datacenterPercent,
        typicalOperation: avgPrice <= curtailmentThreshold ? 'Datacenter Up' : 'Curtailed'
      };
    }).sort((a, b) => a.hour - b.hour);

    // Calculate daily operational metrics
    const dailyAverages = Object.keys(dailyData).map(day => {
      const dayData = dailyData[day];
      const prices = dayData.prices;
      const avgPrice = prices.reduce((a, b) => a + b, 0) / prices.length;
      const totalHours = dayData.datacenterHours + dayData.normalOperatingHours + dayData.gridSaleHours;
      const uptimeHours = dayData.datacenterHours + dayData.normalOperatingHours;
      const uptime = totalHours > 0 ? (uptimeHours / totalHours) * 100 : 0;
      
      return {
        date: day,
        average: avgPrice,
        min: Math.min(...prices),
        max: Math.max(...prices),
        uptime,
        datacenterHours: dayData.datacenterHours,
        normalOperatingHours: dayData.normalOperatingHours,
        gridSaleHours: dayData.gridSaleHours,
        curtailmentRevenue: dayData.revenue,
        hourlyData: dayData.hourlyData,
        totalHours: totalHours
      };
    }).sort((a, b) => new Date(a.date) - new Date(b.date));

    return {
      hourly: hourlyAverages,
      daily: dailyAverages,
      raw: data
    };
  };

  // Chart configurations for datacenter operations
  const getHourlyChart = () => {
    if (!data?.processed?.hourly) return null;

    return {
      labels: data.processed.hourly.map(h => `${h.hour}:00`),
      datasets: [
        {
          label: 'Average Price ($/MWh)',
          data: data.processed.hourly.map(h => h.average),
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: true,
          tension: 0.4
        },
        {
          label: 'Datacenter Uptime %',
          data: data.processed.hourly.map(h => h.datacenterPercent),
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.3)',
          yAxisID: 'y1',
          type: 'bar'
        },
        {
          label: 'Contract Price',
          data: data.processed.hourly.map(() => curtailmentThreshold),
          borderColor: 'rgb(239, 68, 68)',
          borderDash: [10, 5],
          borderWidth: 2,
          fill: false,
          pointRadius: 0
        }
      ]
    };
  };

  const getDailyChart = () => {
    if (!data?.processed?.daily) return null;

    // Generate labels with dates formatted for display
    return {
      labels: data.processed.daily.map(d => {
        const date = new Date(d.date);
        // Return date in a format Chart.js can use
        return format(date, 'MMM d, yyyy');
      }),
      datasets: [
        {
          label: 'Daily Average ($/MWh)',
          data: data.processed.daily.map(d => d.average),  // Keep actual values
          borderColor: 'rgb(139, 92, 246)',
          backgroundColor: 'rgba(139, 92, 246, 0.1)',
          type: 'line',
          tension: 0.4,
          yAxisID: 'y',
          pointRadius: 1,  // Very small points
          pointHoverRadius: 3,  // Slightly larger on hover
          borderWidth: 1.5,
          pointBackgroundColor: 'rgba(139, 92, 246, 0.5)',
          pointBorderColor: 'transparent'
        },
        {
          label: 'Datacenter Uptime %',
          data: data.processed.daily.map(d => d.uptime),
          backgroundColor: 'rgba(34, 197, 94, 0.5)',
          borderColor: 'rgb(34, 197, 94)',
          type: 'bar',
          yAxisID: 'y1',
          borderWidth: 0
        },
      ]
    };
  };

  // Handle daily chart click for detailed view
  const handleChartClick = (event, elements) => {
    if (elements.length > 0 && data?.processed?.daily) {
      const index = elements[0].index;
      const dayData = data.processed.daily[index];
      setSelectedDate(dayData);
      setActiveTab('dayDetail');
    }
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: handleChartClick,
    interaction: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#e2e8f0',
          usePointStyle: true,
          padding: 15,
          generateLabels: (chart) => {
            const original = ChartJS.defaults.plugins.legend.labels.generateLabels;
            const labels = original.call(this, chart);
            // Remove the persistency of legend click by resetting datasets
            labels.forEach(label => {
              label.hidden = chart.getDatasetMeta(label.datasetIndex).hidden;
            });
            return labels;
          }
        },
        onHover: (event, legendItem, legend) => {
          // Highlight dataset on hover
          const chart = legend.chart;
          chart.canvas.style.cursor = 'pointer';
        },
        onLeave: (event, legendItem, legend) => {
          const chart = legend.chart;
          chart.canvas.style.cursor = 'default';
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          title: (tooltipItems) => {
            if (tooltipItems.length > 0 && data?.processed?.daily) {
              const index = tooltipItems[0].dataIndex;
              const dayData = data.processed.daily[index];
              if (dayData) {
                return format(new Date(dayData.date), 'MMM dd, yyyy');
              }
            }
            return tooltipItems[0]?.label || '';
          },
          label: (context) => {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            // Use raw data value instead of parsed value for accurate display
            const rawValue = context.raw;
            if (label.includes('%')) {
              label += `${rawValue.toFixed(1)}%`;
            } else if (label.includes('$')) {
              label += `$${rawValue.toFixed(2)}`;
            } else {
              label += rawValue.toFixed(2);
            }
            return label;
          }
        }
      },
      annotation: {
        annotations: {
          threshold: {
            type: 'line',
            yMin: curtailmentThreshold,
            yMax: curtailmentThreshold,
            borderColor: 'rgb(239, 68, 68)',
            borderWidth: 2,
            borderDash: [5, 5],
            label: {
              enabled: true,
              content: `Curtailment: $${curtailmentThreshold}/MWh`,
              position: 'end'
            }
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          color: 'rgba(148, 163, 184, 0.1)'
        },
        ticks: {
          color: '#94a3b8',
          autoSkip: true,
          source: 'auto',
          maxRotation: 45,
          minRotation: 45,
          callback: function(value, index) {
            // Parse the label and show only month
            const label = this.getLabelForValue(value);
            if (label) {
              const parts = label.split(' ');
              if (parts.length >= 1) {
                // Return "MMM" format in uppercase
                return parts[0].toUpperCase();
              }
            }
            return '';
          }
        }
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        grid: {
          color: 'rgba(148, 163, 184, 0.1)'
        },
        ticks: {
          color: '#94a3b8',
          callback: (value) => `$${value}`
        },
        max: 150  // Cap display at $150 to prevent chart blowout
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        grid: {
          drawOnChartArea: false
        },
        ticks: {
          color: '#94a3b8',
          callback: (value) => `${value}%`
        },
        max: 100
      }
    }
  };

  console.log('About to render, data:', data ? 'exists' : 'null', 'rawData:', rawData ? `${rawData.length} items` : 'null');
  if (data) {
    console.log('data.stats:', data.stats ? 'exists' : 'null');
    console.log('data.processed:', data.processed ? 'exists' : 'null');
  }
  
  return (
    <div className="energy-calculator">
    <div className="container">
      <div className="header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1>Datacenter BTM Cost Recovery Analysis</h1>
            <p>Historical DALMP Impact on Behind-the-Meter Operations</p>
          </div>
          <div>
            <input
              type="file"
              accept=".csv,.tsv"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
              id="file-upload"
            />
            <label htmlFor="file-upload">
              <button className="upload-button" onClick={() => document.getElementById('file-upload').click()}>
                Import Data
              </button>
            </label>
          </div>
        </div>
      </div>

      {loading && (
        <div className="loading">
          <p>Processing data...</p>
        </div>
      )}

      {error && (
        <div className="error">
          <p>{error}</p>
        </div>
      )}

      {data && data.stats && (
        <>
          {/* CONDITIONS SECTION */}
          <div style={{
            background: 'rgba(30, 41, 59, 0.7)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '12px',
            padding: '1.5rem',
            marginBottom: '2rem'
          }}>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem'}}>
              <h3 style={{color: '#3b82f6', margin: 0}}>CONDITIONS: Cooperative Pilot Energy Rate (CPER) Explorer</h3>
              <button
                onClick={() => setShowConditionsInfo(!showConditionsInfo)}
                style={{
                  background: 'rgba(59, 130, 246, 0.2)',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  borderRadius: '6px',
                  padding: '0.25rem 0.75rem',
                  color: '#3b82f6',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <span>{showConditionsInfo ? '▼' : '▶'}</span>
                Understanding the Economics
              </button>
            </div>
            
            {/* Collapsible Info Section */}
            {showConditionsInfo && (
              <div style={{
                background: 'rgba(59, 130, 246, 0.05)',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem',
                fontSize: '0.875rem',
                color: '#e2e8f0'
              }}>
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem'}}>
                  <div>
                    <div style={{color: '#3b82f6', fontWeight: 'bold', marginBottom: '0.5rem'}}>How It Works</div>
                    <div style={{lineHeight: '1.6'}}>
                      • Datacenter pays fixed CPER of ${curtailmentThreshold.toFixed(2)}/MWh<br/>
                      • Operates continuously at 50MW (~90% uptime)<br/>
                      • Curtails for grid sales when DALMP ≥ ${gridSaleThreshold}/MWh<br/>
                      • Emergency curtailments ~5% for SPP reliability
                    </div>
                  </div>
                  <div>
                    <div style={{color: '#10b981', fontWeight: 'bold', marginBottom: '0.5rem'}}>Value Proposition</div>
                    <div style={{lineHeight: '1.6'}}>
                      • Convert idle capacity → productive asset<br/>
                      • Stable ${(plantCapacityMW * opUptimeTarget * hoursPerYear * calculatedCPER / 1000000).toFixed(1)}M/year baseload revenue<br/>
                      • Capture price spikes above ${gridSaleThreshold}/MWh<br/>
                      • Reduce cycling costs from start/stop
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Four Column Layout - inputs in first 2 cols, calculations in cols 3-4 */}
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: '1.5rem', marginBottom: '1.5rem'}}>
              {/* First Column - Variable & Fixed Costs */}
              <div>
                <h4 style={{color: '#94a3b8', marginBottom: '0.75rem', fontSize: '0.8rem'}}>VARIABLE & FIXED COSTS</h4>
                
                {/* Variable Operating Costs Section */}
                <div style={{marginBottom: '1rem'}}>
                  <div style={{color: '#3b82f6', fontSize: '0.7rem', fontWeight: 'bold', marginBottom: '0.5rem'}}>Variable Operating Costs</div>
                  
                  {/* Heat Rate */}
                  <div style={{marginBottom: '0.5rem'}}>
                    <label style={{color: '#94a3b8', fontSize: '0.7rem', display: 'block', marginBottom: '0.25rem'}}>
                      Heat Rate (MMBtu/MWh)
                      <span style={{color: '#64748b', marginLeft: '0.5rem', cursor: 'help'}} title="JTEC's fuel efficiency. Lower is better. Typical subcritical coal: 10.0-10.8">ⓘ</span>
                    </label>
                    <input
                      type="number"
                      min="9.5"
                      max="11.5"
                      step="0.1"
                      value={heatRate}
                      onChange={(e) => setHeatRate(Number(e.target.value))}
                      style={{
                        width: '100%',
                        padding: '0.25rem 0.5rem',
                        background: 'rgba(30, 41, 59, 0.9)',
                        border: '1px solid rgba(148, 163, 184, 0.3)',
                        borderRadius: '4px',
                        color: '#e2e8f0',
                        fontSize: '0.75rem'
                      }}
                    />
                  </div>
                  
                  {/* Coal Price */}
                  <div style={{marginBottom: '0.5rem'}}>
                    <label style={{color: '#94a3b8', fontSize: '0.7rem', display: 'block', marginBottom: '0.25rem'}}>
                      Coal Price ($/MMBtu)
                      <span style={{color: '#64748b', marginLeft: '0.5rem', cursor: 'help'}} title="Powder River Basin coal delivered price. Recent range: $1.40-$1.95/MMBtu">ⓘ</span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="3"
                      step="0.05"
                      value={coalPrice}
                      onChange={(e) => setCoalPrice(Number(e.target.value))}
                      style={{
                        width: '100%',
                        padding: '0.25rem 0.5rem',
                        background: 'rgba(30, 41, 59, 0.9)',
                        border: '1px solid rgba(148, 163, 184, 0.3)',
                        borderRadius: '4px',
                        color: '#e2e8f0',
                        fontSize: '0.75rem'
                      }}
                    />
                  </div>
                  
                  {/* Variable O&M */}
                  <div style={{marginBottom: '0.5rem'}}>
                    <label style={{color: '#94a3b8', fontSize: '0.7rem', display: 'block', marginBottom: '0.25rem'}}>
                      Variable O&M ($/MWh)
                      <span style={{color: '#64748b', marginLeft: '0.5rem', cursor: 'help'}} title="Chemicals, water, consumables, variable labor per MWh generated">ⓘ</span>
                    </label>
                    <input
                      type="number"
                      min="2"
                      max="5"
                      step="0.1"
                      value={variableOM}
                      onChange={(e) => setVariableOM(Number(e.target.value))}
                      style={{
                        width: '100%',
                        padding: '0.25rem 0.5rem',
                        background: 'rgba(30, 41, 59, 0.9)',
                        border: '1px solid rgba(148, 163, 184, 0.3)',
                        borderRadius: '4px',
                        color: '#e2e8f0',
                        fontSize: '0.75rem'
                      }}
                    />
                  </div>
                </div>
                
                {/* Fixed Operating Costs Section */}
                <div style={{marginBottom: '1rem'}}>
                  <div style={{color: '#10b981', fontSize: '0.7rem', fontWeight: 'bold', marginBottom: '0.5rem'}}>Fixed Operating Costs</div>
                  
                  {/* Plant Staff & Labor */}
                  <div style={{marginBottom: '0.5rem'}}>
                    <label style={{color: '#94a3b8', fontSize: '0.7rem', display: 'block', marginBottom: '0.25rem'}}>
                      Plant Staff & Labor ($/MWh)
                      <span style={{color: '#64748b', marginLeft: '0.5rem', cursor: 'help'}} title="Fixed staff costs allocated per MWh. Set >0 if pilot should share fixed labor">ⓘ</span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="8"
                      step="0.25"
                      value={plantStaffLabor}
                      onChange={(e) => setPlantStaffLabor(Number(e.target.value))}
                      style={{
                        width: '100%',
                        padding: '0.25rem 0.5rem',
                        background: 'rgba(30, 41, 59, 0.9)',
                        border: '1px solid rgba(148, 163, 184, 0.3)',
                        borderRadius: '4px',
                        color: '#e2e8f0',
                        fontSize: '0.75rem'
                      }}
                    />
                  </div>
                  
                  {/* Maintenance Contracts */}
                  <div style={{marginBottom: '0.5rem'}}>
                    <label style={{color: '#94a3b8', fontSize: '0.7rem', display: 'block', marginBottom: '0.25rem'}}>
                      Maintenance Contracts ($/MWh)
                      <span style={{color: '#64748b', marginLeft: '0.5rem', cursor: 'help'}} title="Annual maintenance contracts allocated per MWh. Set >0 to include">ⓘ</span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="5"
                      step="0.25"
                      value={maintenanceContracts}
                      onChange={(e) => setMaintenanceContracts(Number(e.target.value))}
                      style={{
                        width: '100%',
                        padding: '0.25rem 0.5rem',
                        background: 'rgba(30, 41, 59, 0.9)',
                        border: '1px solid rgba(148, 163, 184, 0.3)',
                        borderRadius: '4px',
                        color: '#e2e8f0',
                        fontSize: '0.75rem'
                      }}
                    />
                  </div>
                </div>
                
                {/* Environmental & Grid Section */}
                <div style={{marginBottom: '1rem'}}>
                  <div style={{color: '#8b5cf6', fontSize: '0.7rem', fontWeight: 'bold', marginBottom: '0.5rem'}}>Environmental & Grid</div>
                  
                  {/* Environmental Compliance */}
                  <div style={{marginBottom: '0.5rem'}}>
                    <label style={{color: '#94a3b8', fontSize: '0.7rem', display: 'block', marginBottom: '0.25rem'}}>
                      Environmental Compliance ($/MWh)
                      <span style={{color: '#64748b', marginLeft: '0.5rem', cursor: 'help'}} title="Emissions allowances, ash disposal, environmental controls. Set >0 to include">ⓘ</span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="5"
                      step="0.25"
                      value={environmentalCompliance}
                      onChange={(e) => setEnvironmentalCompliance(Number(e.target.value))}
                      style={{
                        width: '100%',
                        padding: '0.25rem 0.5rem',
                        background: 'rgba(30, 41, 59, 0.9)',
                        border: '1px solid rgba(148, 163, 184, 0.3)',
                        borderRadius: '4px',
                        color: '#e2e8f0',
                        fontSize: '0.75rem'
                      }}
                    />
                  </div>
                  
                  {/* Grid & Admin Costs */}
                  <div style={{marginBottom: '0.5rem'}}>
                    <label style={{color: '#94a3b8', fontSize: '0.7rem', display: 'block', marginBottom: '0.25rem'}}>
                      Grid & Admin Costs ($/MWh)
                      <span style={{color: '#64748b', marginLeft: '0.5rem', cursor: 'help'}} title="SPP charges, NERC compliance, overhead allocation. Set >0 to include">ⓘ</span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="5"
                      step="0.25"
                      value={gridAdminCosts}
                      onChange={(e) => setGridAdminCosts(Number(e.target.value))}
                      style={{
                        width: '100%',
                        padding: '0.25rem 0.5rem',
                        background: 'rgba(30, 41, 59, 0.9)',
                        border: '1px solid rgba(148, 163, 184, 0.3)',
                        borderRadius: '4px',
                        color: '#e2e8f0',
                        fontSize: '0.75rem'
                      }}
                    />
                  </div>
                </div>
              </div>
              
              {/* Second Column - CU Avoidances only */}
              <div>
                <h4 style={{color: '#94a3b8', marginBottom: '0.75rem', fontSize: '0.8rem'}}>AVOIDANCES</h4>
                
                {/* CU Cost Avoidances Input Section */}
                <div style={{marginBottom: '0.5rem', borderTop: '1px solid rgba(148, 163, 184, 0.2)', paddingTop: '0.75rem'}}>
                  <div style={{color: '#10b981', fontSize: '0.7rem', fontWeight: 'bold', marginBottom: '0.5rem'}}>CU Cost Avoidances</div>
                  
                  {/* Cold Start Avoidance Inputs */}
                  <div style={{marginBottom: '0.5rem'}}>
                    <label style={{color: '#94a3b8', fontSize: '0.7rem', display: 'block', marginBottom: '0.25rem'}}>
                      Savings Per Cold Start Avoided ($)
                      <span style={{color: '#64748b', marginLeft: '0.5rem', cursor: 'help'}} title="Cost saved per cold start avoided (typical $70K-$100K)">ⓘ</span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="200000"
                      step="5000"
                      value={savingsPerColdStart}
                      onChange={(e) => setSavingsPerColdStart(Number(e.target.value))}
                      style={{
                        width: '100%',
                        padding: '0.25rem 0.5rem',
                        background: 'rgba(30, 41, 59, 0.9)',
                        border: '1px solid rgba(148, 163, 184, 0.3)',
                        borderRadius: '4px',
                        color: '#e2e8f0',
                        fontSize: '0.75rem'
                      }}
                    />
                  </div>
                  
                  <div style={{marginBottom: '0.5rem'}}>
                    <label style={{color: '#94a3b8', fontSize: '0.7rem', display: 'block', marginBottom: '0.25rem'}}>
                      Cold Starts Avoided Yearly
                      <span style={{color: '#64748b', marginLeft: '0.5rem', cursor: 'help'}} title="Number of cold starts avoided per year (typical 10-15)">ⓘ</span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="50"
                      step="1"
                      value={coldStartsAvoidedYearly}
                      onChange={(e) => setColdStartsAvoidedYearly(Number(e.target.value))}
                      style={{
                        width: '100%',
                        padding: '0.25rem 0.5rem',
                        background: 'rgba(30, 41, 59, 0.9)',
                        border: '1px solid rgba(148, 163, 184, 0.3)',
                        borderRadius: '4px',
                        color: '#e2e8f0',
                        fontSize: '0.75rem'
                      }}
                    />
                  </div>
                  
                  {/* Wear and Tear Savings */}
                  <div style={{marginBottom: '0.5rem'}}>
                    <label style={{color: '#94a3b8', fontSize: '0.7rem', display: 'block', marginBottom: '0.25rem'}}>
                      Wear & Tear Savings Over 5 Years ($)
                      <span style={{color: '#64748b', marginLeft: '0.5rem', cursor: 'help'}} title="Total maintenance savings over 5-year pilot period">ⓘ</span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="10000000"
                      step="100000"
                      value={wearTearSavings5Year}
                      onChange={(e) => setWearTearSavings5Year(Number(e.target.value))}
                      style={{
                        width: '100%',
                        padding: '0.25rem 0.5rem',
                        background: 'rgba(30, 41, 59, 0.9)',
                        border: '1px solid rgba(148, 163, 184, 0.3)',
                        borderRadius: '4px',
                        color: '#e2e8f0',
                        fontSize: '0.75rem'
                      }}
                    />
                  </div>
                  
                  {/* Heat Rate Improvement Inputs */}
                  <div style={{marginBottom: '0.5rem'}}>
                    <label style={{color: '#94a3b8', fontSize: '0.7rem', display: 'block', marginBottom: '0.25rem'}}>
                      Base Heat Rate - Cycling (BTU/kWh)
                      <span style={{color: '#64748b', marginLeft: '0.5rem', cursor: 'help'}} title="Heat rate during normal cycling operation">ⓘ</span>
                    </label>
                    <input
                      type="number"
                      min="8000"
                      max="12000"
                      step="50"
                      value={baseHeatRate}
                      onChange={(e) => setBaseHeatRate(Number(e.target.value))}
                      style={{
                        width: '100%',
                        padding: '0.25rem 0.5rem',
                        background: 'rgba(30, 41, 59, 0.9)',
                        border: '1px solid rgba(148, 163, 184, 0.3)',
                        borderRadius: '4px',
                        color: '#e2e8f0',
                        fontSize: '0.75rem'
                      }}
                    />
                  </div>
                  
                  <div style={{marginBottom: '0.5rem'}}>
                    <label style={{color: '#94a3b8', fontSize: '0.7rem', display: 'block', marginBottom: '0.25rem'}}>
                      Improved Heat Rate - Steady (BTU/kWh)
                      <span style={{color: '#64748b', marginLeft: '0.5rem', cursor: 'help'}} title="Heat rate with steady datacenter operation">ⓘ</span>
                    </label>
                    <input
                      type="number"
                      min="8000"
                      max="12000"
                      step="50"
                      value={improvedHeatRate}
                      onChange={(e) => setImprovedHeatRate(Number(e.target.value))}
                      style={{
                        width: '100%',
                        padding: '0.25rem 0.5rem',
                        background: 'rgba(30, 41, 59, 0.9)',
                        border: '1px solid rgba(148, 163, 184, 0.3)',
                        borderRadius: '4px',
                        color: '#e2e8f0',
                        fontSize: '0.75rem'
                      }}
                    />
                  </div>
                  
                  <div style={{marginBottom: '0.5rem'}}>
                    <label style={{color: '#94a3b8', fontSize: '0.7rem', display: 'block', marginBottom: '0.25rem'}}>
                      Annual Generation (MWh/year)
                      <span style={{color: '#64748b', marginLeft: '0.5rem', cursor: 'help'}} title="Expected annual generation: MW × CF × 8760">ⓘ</span>
                    </label>
                    <input
                      type="number"
                      min="50000"
                      max="500000"
                      step="1000"
                      value={annualGenerationMWh}
                      onChange={(e) => setAnnualGenerationMWh(Number(e.target.value))}
                      style={{
                        width: '100%',
                        padding: '0.25rem 0.5rem',
                        background: 'rgba(30, 41, 59, 0.9)',
                        border: '1px solid rgba(148, 163, 184, 0.3)',
                        borderRadius: '4px',
                        color: '#e2e8f0',
                        fontSize: '0.75rem'
                      }}
                    />
                  </div>
                </div>
                
                {/* Contract Structure Section */}
                <div style={{marginBottom: '0.5rem', borderTop: '1px solid rgba(148, 163, 184, 0.2)', paddingTop: '0.75rem'}}>
                  <div style={{color: '#ef4444', fontSize: '0.7rem', fontWeight: 'bold', marginBottom: '0.5rem'}}>Contract Structure</div>
                  
                  <div style={{marginBottom: '0.5rem'}}>
                    <label style={{color: '#94a3b8', fontSize: '0.7rem', display: 'block', marginBottom: '0.25rem'}}>
                      Margin Above Cost ($/MWh)
                      <span style={{color: '#64748b', marginLeft: '0.5rem', cursor: 'help'}} title="Optional margin or risk premium above calculated costs">ⓘ</span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="5"
                      step="0.25"
                      value={marginAboveCost}
                      onChange={(e) => setMarginAboveCost(Number(e.target.value))}
                      style={{
                        width: '100%',
                        padding: '0.25rem 0.5rem',
                        background: 'rgba(30, 41, 59, 0.9)',
                        border: '1px solid rgba(148, 163, 184, 0.3)',
                        borderRadius: '4px',
                        color: '#e2e8f0',
                        fontSize: '0.75rem'
                      }}
                    />
                  </div>
                </div>
              </div>
              
              {/* Right Column - Calculations */}
              <div>
                <h4 style={{color: '#94a3b8', marginBottom: '1rem', fontSize: '0.9rem'}}>CPER CALCULATION</h4>
                
                {/* Generation Cost Build-Up */}
                <div style={{marginBottom: '0.75rem'}}>
                  <div style={{color: '#3b82f6', fontSize: '0.7rem', fontWeight: 'bold', marginBottom: '0.5rem'}}>Generation Cost Build-Up</div>
                  
                  {/* Fuel Cost */}
                  <div style={{marginBottom: '0.4rem'}}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                      <label style={{color: '#94a3b8', fontSize: '0.7rem'}}>
                        Fuel Cost
                        <span style={{color: '#64748b', marginLeft: '0.5rem', cursor: 'help'}} title={`Heat Rate (${heatRate.toFixed(1)}) × Coal Price ($${coalPrice.toFixed(2)}) = Fuel cost per MWh`}>ⓘ</span>
                      </label>
                      <div style={{color: '#ef4444', fontSize: '0.875rem', fontWeight: 'bold'}}>
                        ${(heatRate * coalPrice).toFixed(2)}/MWh
                      </div>
                    </div>
                  </div>
                  
                  {/* Variable O&M */}
                  <div style={{marginBottom: '0.4rem'}}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                      <label style={{color: '#94a3b8', fontSize: '0.7rem'}}>
                        Variable O&M
                        <span style={{color: '#64748b', marginLeft: '0.5rem', cursor: 'help'}} title={`Variable Operations & Maintenance costs`}>ⓘ</span>
                      </label>
                      <div style={{color: '#10b981', fontSize: '0.875rem', fontWeight: 'bold'}}>
                        ${variableOM.toFixed(2)}/MWh
                      </div>
                    </div>
                  </div>
                  
                  {/* Total Fixed Costs */}
                  <div style={{marginBottom: '0.4rem'}}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                      <label style={{color: '#94a3b8', fontSize: '0.7rem'}}>
                        Total Fixed Costs
                        <span style={{color: '#64748b', marginLeft: '0.5rem', cursor: 'help'}} title={`Staff ($${plantStaffLabor.toFixed(2)}) + Maintenance ($${maintenanceContracts.toFixed(2)}) = Fixed operating costs`}>ⓘ</span>
                      </label>
                      <div style={{color: (plantStaffLabor + maintenanceContracts) > 0 ? '#f59e0b' : '#64748b', fontSize: '0.875rem', fontWeight: 'bold'}}>
                        ${(plantStaffLabor + maintenanceContracts).toFixed(2)}/MWh
                      </div>
                    </div>
                  </div>
                  
                  {/* Capacity & Capital */}
                  <div style={{marginBottom: '0.4rem'}}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                      <label style={{color: '#94a3b8', fontSize: '0.7rem'}}>
                        Capacity & Capital
                        <span style={{color: '#64748b', marginLeft: '0.5rem', cursor: 'help'}} title="Capital recovery if included in pilot rate">ⓘ</span>
                      </label>
                      <div style={{color: capacityRecovery > 0 ? '#f59e0b' : '#64748b', fontSize: '0.875rem', fontWeight: 'bold'}}>
                        ${capacityRecovery.toFixed(2)}/MWh
                      </div>
                    </div>
                  </div>
                  
                  {/* Environmental & Grid */}
                  <div style={{marginBottom: '0.4rem'}}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                      <label style={{color: '#94a3b8', fontSize: '0.7rem'}}>
                        Environmental & Grid
                        <span style={{color: '#64748b', marginLeft: '0.5rem', cursor: 'help'}} title={`Environmental ($${environmentalCompliance.toFixed(2)}) + Grid costs ($${gridAdminCosts.toFixed(2)}) if included`}>ⓘ</span>
                      </label>
                      <div style={{color: (environmentalCompliance + gridAdminCosts) > 0 ? '#8b5cf6' : '#64748b', fontSize: '0.875rem', fontWeight: 'bold'}}>
                        ${(environmentalCompliance + gridAdminCosts).toFixed(2)}/MWh
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Full Generation Cost - Highlighted */}
                <div style={{marginBottom: '0.75rem', borderTop: '1px solid rgba(148, 163, 184, 0.2)', paddingTop: '0.5rem'}}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                    <label style={{color: '#e2e8f0', fontSize: '0.75rem', fontWeight: 'bold'}}>
                      Full Generation Cost
                      <span style={{color: '#64748b', marginLeft: '0.5rem', cursor: 'help'}} title="Total cost to generate including selected components">ⓘ</span>
                    </label>
                    <div style={{
                      color: '#3b82f6',
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      padding: '0.25rem 0.5rem',
                      background: 'rgba(59, 130, 246, 0.1)',
                      borderRadius: '4px'
                    }}>
                      ${(heatRate * coalPrice + variableOM + plantStaffLabor + maintenanceContracts + capacityRecovery + environmentalCompliance + gridAdminCosts).toFixed(2)}/MWh
                    </div>
                  </div>
                </div>
                
                {/* CU Cost Avoidances */}
                <div style={{marginBottom: '0.75rem', padding: '0.75rem', background: 'rgba(16, 185, 129, 0.1)', borderRadius: '6px', border: '1px solid rgba(16, 185, 129, 0.2)'}}>
                  <label style={{color: '#10b981', fontSize: '0.7rem', display: 'block', marginBottom: '0.5rem', fontWeight: 'bold'}}>
                    CU Cost Avoidances (Deducted)
                    <span style={{color: '#64748b', marginLeft: '0.5rem', cursor: 'help'}} title="Value of avoided cycling costs from steady datacenter operation">ⓘ</span>
                  </label>
                  <div style={{fontSize: '0.7rem', color: '#94a3b8', lineHeight: '1.4'}}>
                    <div title={`${coldStartsAvoidedYearly} starts × $${savingsPerColdStart.toLocaleString()} = $${coldStartAvoidance.toLocaleString()}/yr ÷ ${annualGenerationMWh.toLocaleString()} MWh/yr`}>
                      • Avoided Cold Starts: ${(coldStartAvoidance / 1000000).toFixed(2)}M/yr = ${(coldStartAvoidance / annualGenerationMWh).toFixed(2)}/MWh
                    </div>
                    <div title={`$${wearTearSavings5Year.toLocaleString()} over 5 years ÷ 5 = $${cyclingWearSavings.toLocaleString()}/yr ÷ ${annualGenerationMWh.toLocaleString()} MWh/yr`}>
                      • Reduced Cycling Wear: ${(cyclingWearSavings / 1000000).toFixed(2)}M/yr = ${(cyclingWearSavings / annualGenerationMWh).toFixed(2)}/MWh
                    </div>
                    <div title={`${heatRateImprovementPercent.toFixed(1)}% improvement × ${annualGenerationMWh.toLocaleString()} MWh × $${(heatRate * coalPrice).toFixed(2)}/MWh fuel cost`}>
                      • Heat Rate Improvements ({heatRateImprovementPercent.toFixed(1)}%): ${(heatRateImprovements / 1000000).toFixed(2)}M/yr = ${(heatRateImprovements / annualGenerationMWh).toFixed(2)}/MWh
                    </div>
                    <div style={{marginTop: '0.25rem', paddingTop: '0.25rem', borderTop: '1px solid rgba(148, 163, 184, 0.2)', fontWeight: 'bold', color: '#10b981'}}>
                      Total Avoidances: ${((coldStartAvoidance + cyclingWearSavings + heatRateImprovements) / 1000000).toFixed(2)}M/yr = ${((coldStartAvoidance + cyclingWearSavings + heatRateImprovements) / annualGenerationMWh).toFixed(2)}/MWh
                    </div>
                  </div>
                </div>
                
                {/* Contract Rate Calculation */}
                <div style={{marginBottom: '0.75rem', borderTop: '1px solid rgba(148, 163, 184, 0.2)', paddingTop: '0.5rem'}}>
                  <div style={{color: '#e2e8f0', fontSize: '0.7rem', fontWeight: 'bold', marginBottom: '0.5rem'}}>Contract Rate Calculation</div>
                  
                  {/* Net Cost After Benefits */}
                  <div style={{marginBottom: '0.4rem'}}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                      <label style={{color: '#94a3b8', fontSize: '0.7rem'}}>
                        Net Cost After Benefits
                        <span style={{color: '#64748b', marginLeft: '0.5rem', cursor: 'help'}} title="Generation cost minus value of avoided cycling">ⓘ</span>
                      </label>
                      <div style={{color: '#e2e8f0', fontSize: '0.875rem'}}>
                        ${Math.max(0, (heatRate * coalPrice + variableOM + plantStaffLabor + maintenanceContracts + capacityRecovery + environmentalCompliance + gridAdminCosts - (coldStartAvoidance + cyclingWearSavings + heatRateImprovements) / annualGenerationMWh)).toFixed(2)}/MWh
                      </div>
                    </div>
                  </div>
                  
                  {/* Plus Margin */}
                  <div style={{marginBottom: '0.4rem'}}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                      <label style={{color: '#94a3b8', fontSize: '0.7rem'}}>
                        Plus Margin
                        <span style={{color: '#64748b', marginLeft: '0.5rem', cursor: 'help'}} title="Optional margin or risk premium">ⓘ</span>
                      </label>
                      <div style={{color: marginAboveCost > 0 ? '#ef4444' : '#64748b', fontSize: '0.875rem'}}>
                        +${marginAboveCost.toFixed(2)}/MWh
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Final CPER Rate - Big Display */}
                <div style={{
                  padding: '0.75rem',
                  background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(139, 92, 246, 0.2))',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  borderRadius: '6px',
                  marginBottom: '0.75rem'
                }}>
                  <label style={{color: '#3b82f6', fontSize: '0.7rem', display: 'block', marginBottom: '0.25rem', fontWeight: 'bold'}}>CPER Rate</label>
                  <div style={{
                    color: '#3b82f6',
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    textAlign: 'center'
                  }}>
                    ${Math.max(0, (heatRate * coalPrice + variableOM + plantStaffLabor + maintenanceContracts + capacityRecovery + environmentalCompliance + gridAdminCosts - (coldStartAvoidance + cyclingWearSavings + heatRateImprovements) / annualGenerationMWh + marginAboveCost)).toFixed(2)}/MWh
                  </div>
                  <div style={{fontSize: '0.65rem', color: '#64748b', textAlign: 'center', marginTop: '0.25rem'}}>
                    Cooperative Pilot Energy Rate - the contract price
                  </div>
                </div>
                
                {/* Annual Impact */}
                <div style={{fontSize: '0.7rem', color: '#94a3b8', borderTop: '1px solid rgba(148, 163, 184, 0.2)', paddingTop: '0.5rem'}}>
                  <div style={{marginBottom: '0.25rem'}}>
                    <strong>CU Annual Revenue:</strong> ${(annualGenerationMWh * calculatedCPER / 1000000).toFixed(1)}M
                    <span style={{color: '#64748b', marginLeft: '0.5rem', cursor: 'help'}} title={`${plantCapacityMW}MW × ${(opUptimeTarget * 100).toFixed(0)}% uptime × ${hoursPerYear} hrs × CPER`}>ⓘ</span>
                  </div>
                  <div>
                    <strong>Total CU Value:</strong> ${(annualGenerationMWh * calculatedCPER / 1000000 + 3.1).toFixed(1)}M
                    <span style={{color: '#64748b', marginLeft: '0.5rem', cursor: 'help'}} title="Contract revenue plus $3.1M avoided cycling costs">ⓘ</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Sliders Section */}
            <div style={{borderTop: '1px solid rgba(148, 163, 184, 0.2)', paddingTop: '1rem'}}>
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem'}}>
                {/* CPER Display (Calculated, not adjustable) */}
                <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                  <span style={{color: '#94a3b8', fontSize: '0.875rem', minWidth: '90px'}}>CPER Rate:</span>
                  <div style={{
                    height: '32px',
                    minWidth: '120px',
                    background: calculatedCPER < 15 ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' :
                               calculatedCPER > 25 ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' :
                               'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 0.75rem',
                    fontWeight: 'bold',
                    color: 'white'
                  }}>
                    ${calculatedCPER.toFixed(2)}/MWh
                  </div>
                  <span style={{color: '#64748b', fontSize: '0.75rem', flex: 1}}>
                    {calculatedCPER < 10 ? 'Below cost recovery' :
                     calculatedCPER < 15 ? 'Highly competitive' :
                     calculatedCPER <= 25 ? '' :
                     'May be uncompetitive'}
                  </span>
                </div>
                
                {/* Grid Sale Slider */}
                <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                  <span style={{color: '#94a3b8', fontSize: '0.875rem', minWidth: '90px'}}>Grid Sale:</span>
                  <div style={{
                    height: '32px',
                    minWidth: '100px',
                    background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 0.75rem',
                    fontWeight: 'bold',
                    color: 'white'
                  }}>
                    ${gridSaleThreshold}/MWh
                  </div>
                  <input
                    type="range"
                    min="40"
                    max="150"
                    step="5"
                    value={gridSaleThreshold}
                    onChange={(e) => setGridSaleThreshold(Number(e.target.value))}
                    style={{flex: 1}}
                  />
                  <span style={{color: '#94a3b8', fontSize: '0.75rem'}}>Curtail trigger</span>
                </div>
              </div>
            </div>
          </div>

          {/* ANALYSIS SECTION - Daily Performance */}

          <div className="charts-section">
            {false && (
              <div className="two-column">
                <div className="chart-container">
                  <h2>Typical Day: Datacenter vs Grid Sale Hours</h2>
                  <div className="chart-wrapper">
                    {getHourlyChart() && (
                      <Line data={getHourlyChart()} options={chartOptions} />
                    )}
                  </div>
                </div>
                <div className="chart-container">
                  <h2>Daily Performance (Click for Details)</h2>
                  <div className="chart-wrapper">
                    {getDailyChart() && (
                      <Line data={getDailyChart()} options={{...chartOptions, scales: {...chartOptions.scales}}} />
                    )}
                  </div>
                </div>
              </div>
            )}

            {false && (
              <div className="chart-container">
                <h2>24-Hour Operational Pattern</h2>
                <p style={{color: '#94a3b8', marginBottom: '1rem'}}>
                  Shows typical datacenter uptime percentage by hour. Peak afternoon hours often trigger curtailment.
                </p>
                <div className="chart-wrapper">
                  {getHourlyChart() && (
                    <Line data={getHourlyChart()} options={chartOptions} />
                  )}
                </div>
              </div>
            )}

            {/* Daily Performance - Main View */}
            <div style={{
                background: 'rgba(30, 41, 59, 0.5)',
                borderRadius: '12px',
                border: '1px solid rgba(148, 163, 184, 0.2)',
                overflow: 'hidden'
              }}>
                {/* Chart content */}
                <div style={{padding: '1.5rem', paddingBottom: '0.5rem'}}>
                  <h2>Pilot Performance on Historical Data Selection</h2>
                  <p style={{color: '#94a3b8', marginBottom: '1rem'}}>
                    Purple line shows daily average DALMP prices. Green bars show datacenter uptime percentage.
                  </p>
                  <div className="chart-wrapper">
                    {getDailyChart() && (
                      <Bar data={getDailyChart()} options={chartOptions} />
                    )}
                  </div>
                </div>
                
                {/* 5-Year Window Selector integrated at bottom */}
                <div style={{padding: '0 1.5rem 1.5rem 1.5rem'}}>
                  <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem', paddingLeft: '3rem', paddingRight: '3rem'}}>
                    {[2014, 2015, 2016, 2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024].map(year => (
                      <span key={year} style={{color: '#94a3b8', fontSize: '0.7rem'}}>{year}</span>
                    ))}
                  </div>
                  <div style={{marginLeft: '3rem', marginRight: '3rem', position: 'relative'}}>
                    <div style={{position: 'relative', height: '40px', background: 'rgba(148, 163, 184, 0.1)', borderRadius: '6px', padding: '3px'}}>
                      {/* Monthly tick marks - all 120 months */}
                      <div style={{position: 'absolute', width: '100%', height: '100%', pointerEvents: 'none'}}>
                        {[...Array(121)].map((_, i) => (
                          <div key={i} style={{
                            position: 'absolute',
                            left: `${(i / 120) * 100}%`,
                            width: '1px',
                            height: i % 12 === 0 ? '12px' : '6px',
                            bottom: '0',
                            background: i % 12 === 0 ? 'rgba(148, 163, 184, 0.4)' : 'rgba(148, 163, 184, 0.2)'
                          }} />
                        ))}
                      </div>
                      <div style={{
                        position: 'absolute',
                        left: `${(tempMonthWindow / 60) * 50}%`,
                        width: '50%',
                        height: '28px',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                        borderRadius: '4px',
                        transition: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.75rem',
                        boxShadow: '0 1px 4px rgba(59, 130, 246, 0.3)',
                        top: '3px',
                        pointerEvents: 'none'
                      }}>
                        {['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'][tempMonthWindow % 12]} {2014 + Math.floor(tempMonthWindow/12)} - {['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'][(tempMonthWindow + 59) % 12]} {2014 + Math.floor((tempMonthWindow + 59)/12)}
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="60"
                        step="1"
                        value={tempMonthWindow}
                        onChange={(e) => setTempMonthWindow(Number(e.target.value))}
                        onMouseUp={(e) => setMonthWindow(Number(e.target.value))}
                        onTouchEnd={(e) => setMonthWindow(Number(e.target.value))}
                        style={{
                          position: 'absolute',
                          width: '100%',
                          height: '100%',
                          opacity: 0,
                          cursor: 'pointer',
                          zIndex: 1
                        }}
                      />
                    </div>
                  </div>
                  <div style={{textAlign: 'center', marginTop: '0.5rem', color: '#64748b', fontSize: '0.75rem'}}>
                    5-Year Pilot Window Selection (Monthly Precision)
                  </div>
                </div>
              </div>

            {selectedDate && (
              <div className="chart-container" style={{position: 'relative'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <h2>Hourly Operations: {format(new Date(selectedDate.date), 'MMMM dd, yyyy')}</h2>
                  <button
                    onClick={() => setSelectedDate(null)}
                    style={{
                      background: 'transparent',
                      border: '1px solid rgba(148, 163, 184, 0.3)',
                      color: '#94a3b8',
                      fontSize: '1.2rem',
                      width: '32px',
                      height: '32px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onMouseOver={(e) => e.target.style.backgroundColor = 'rgba(148, 163, 184, 0.1)'}
                    onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    ×
                  </button>
                </div>
                <div style={{display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '2rem'}}>
                  <div>
                    <div className="metric-card">
                      <div className="metric-label">Day Summary</div>
                      <div style={{marginTop: '1rem'}}>
                        <p>Datacenter Up: {selectedDate.datacenterHours} hours ({selectedDate.uptime.toFixed(1)}%)</p>
                        <p>Curtailed: {selectedDate.curtailedHours} hours</p>
                        <p>Avg Price: ${selectedDate.average.toFixed(2)}/MWh</p>
                        <p>Price Range: ${selectedDate.min.toFixed(2)} - ${selectedDate.max.toFixed(2)}</p>
                        <p>Curtailment Value: ${selectedDate.curtailmentRevenue.toFixed(2)}/MW</p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="hourly-breakdown" style={{maxHeight: '400px', overflowY: 'auto'}}>
                      <table style={{width: '100%', borderCollapse: 'collapse'}}>
                        <thead>
                          <tr style={{borderBottom: '1px solid #475569'}}>
                            <th style={{padding: '0.5rem', textAlign: 'left'}}>Hour</th>
                            <th style={{padding: '0.5rem', textAlign: 'left'}}>Price</th>
                            <th style={{padding: '0.5rem', textAlign: 'left'}}>Status</th>
                            <th style={{padding: '0.5rem', textAlign: 'left'}}>Revenue</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedDate.hourlyData.map((hour, idx) => (
                            <tr key={idx} style={{
                              backgroundColor: hour.isDatacenterUp ? 'rgba(34, 197, 94, 0.1)' : 'rgba(251, 146, 60, 0.1)',
                              borderBottom: '1px solid #334155'
                            }}>
                              <td style={{padding: '0.5rem'}}>{hour.hour}:00</td>
                              <td style={{padding: '0.5rem'}}>${hour.price.toFixed(2)}</td>
                              <td style={{padding: '0.5rem'}}>
                                {hour.isDatacenterUp ? 'Datacenter Up' : 'Grid Sale'}
                              </td>
                              <td style={{padding: '0.5rem'}}>
                                {hour.isDatacenterUp ? 
                                  `Operating @ $${curtailmentThreshold}` : 
                                  `+$${(hour.price - curtailmentThreshold).toFixed(2)}/MW`
                                }
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* PERFORMANCE SECTION - Bottom Metrics */}
          <div style={{
            background: 'rgba(30, 41, 59, 0.7)',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            borderRadius: '12px',
            padding: '1.5rem',
            marginTop: '2rem'
          }}>
            <h3 style={{color: '#10b981', marginBottom: '1rem'}}>
              PERFORMANCE: {monthAbbreviations[(monthWindow % 12) + 1]} {2014 + Math.floor(monthWindow/12)} - {monthAbbreviations[((monthWindow + 60) % 12) + 1]} {2014 + Math.floor((monthWindow + 60)/12)} Window
            </h3>
            <div className="metrics-grid">
              <div className="metric-card">
                <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                  <div className="metric-label">Datacenter Uptime</div>
                  <div style={{position: 'relative', display: 'inline-block'}}>
                    <span style={{color: '#3b82f6', cursor: 'help', fontSize: '0.875rem'}}>ℹ️</span>
                    <div className="tooltip" style={{
                      position: 'absolute',
                      bottom: '20px',
                      left: '-100px',
                      width: '200px',
                      padding: '0.5rem',
                      background: 'rgba(30, 41, 59, 0.95)',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      borderRadius: '6px',
                      fontSize: '0.7rem',
                      color: '#e2e8f0',
                      display: 'none',
                      zIndex: 1000,
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                    }}>
                      Percentage of hours datacenter operates at 50MW. Datacenter runs continuously at CPER rate except during: emergency curtailments (~5%) and grid sale events (when DALMP ≥ ${gridSaleThreshold}/MWh).
                    </div>
                  </div>
                </div>
                <div className="metric-value">{data.stats.uptimePercent.toFixed(1)}%</div>
                <div className="metric-change positive">~{Math.round(data.stats.uptimePercent * 0.01 * 8760)}hrs/year avg</div>
              </div>
              <div className="metric-card">
                <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                  <div className="metric-label">Grid Sale Hours</div>
                  <div style={{position: 'relative', display: 'inline-block'}}>
                    <span style={{color: '#3b82f6', cursor: 'help', fontSize: '0.875rem'}}>ℹ️</span>
                    <div className="tooltip" style={{
                      position: 'absolute',
                      bottom: '20px',
                      left: '-100px',
                      width: '200px',
                      padding: '0.5rem',
                      background: 'rgba(30, 41, 59, 0.95)',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      borderRadius: '6px',
                      fontSize: '0.7rem',
                      color: '#e2e8f0',
                      display: 'none',
                      zIndex: 1000,
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                    }}>
                      Total hours when DALMP ≥ ${gridSaleThreshold}/MWh, triggering curtailment to sell 50MW to grid at market prices.
                    </div>
                  </div>
                </div>
                <div className="metric-value">{data.stats.gridSaleHours}</div>
                <div className="metric-change" style={{color: '#94a3b8'}}>When DALMP ≥ ${gridSaleThreshold}/MWh</div>
              </div>
              <div className="metric-card">
                <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                  <div className="metric-label">Utility Revenue</div>
                  <div style={{position: 'relative', display: 'inline-block'}}>
                    <span style={{color: '#3b82f6', cursor: 'help', fontSize: '0.875rem'}}>ℹ️</span>
                    <div className="tooltip" style={{
                      position: 'absolute',
                      bottom: '20px',
                      left: '-100px',
                      width: '200px',
                      padding: '0.5rem',
                      background: 'rgba(30, 41, 59, 0.95)',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      borderRadius: '6px',
                      fontSize: '0.7rem',
                      color: '#e2e8f0',
                      display: 'none',
                      zIndex: 1000,
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                    }}>
                      Annual revenue to CU from flat-rate contract payments: 50MW × {Math.round(data.stats.uptimePercent)}% uptime × 8760 hrs/yr × ${calculatedCPER.toFixed(2)}/MWh
                    </div>
                  </div>
                </div>
                <div className="metric-value">${(50 * (data.stats.uptimePercent / 100) * 8760 * calculatedCPER / 1000000).toFixed(1)}M</div>
                <div className="metric-change">Annual contract revenue</div>
              </div>
              <div className="metric-card">
                <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                  <div className="metric-label">Cold Start Savings</div>
                  <div style={{position: 'relative', display: 'inline-block'}}>
                    <span style={{color: '#3b82f6', cursor: 'help', fontSize: '0.875rem'}}>ℹ️</span>
                    <div className="tooltip" style={{
                      position: 'absolute',
                      bottom: '20px',
                      left: '-100px',
                      width: '200px',
                      padding: '0.5rem',
                      background: 'rgba(30, 41, 59, 0.95)',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      borderRadius: '6px',
                      fontSize: '0.7rem',
                      color: '#e2e8f0',
                      display: 'none',
                      zIndex: 1000,
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                    }}>
                      DataFlex pilot avoids 12-15 cold starts annually (vs 18-22 baseline). Each cold start costs ~$30K (Unit 1: $28.5K, Unit 2: $31.5K). Direct savings: $388K-472K/year.
                    </div>
                  </div>
                </div>
                <div className="metric-value">${(0.43).toFixed(1)}M</div>
                <div className="metric-change">12-15 avoided starts/year</div>
              </div>
              <div className="metric-card">
                <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                  <div className="metric-label">JTEC Uptime Improvement</div>
                  <div style={{position: 'relative', display: 'inline-block'}}>
                    <span style={{color: '#3b82f6', cursor: 'help', fontSize: '0.875rem'}}>ℹ️</span>
                    <div className="tooltip" style={{
                      position: 'absolute',
                      bottom: '20px',
                      left: '-100px',
                      width: '200px',
                      padding: '0.5rem',
                      background: 'rgba(30, 41, 59, 0.95)',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      borderRadius: '6px',
                      fontSize: '0.7rem',
                      color: '#e2e8f0',
                      display: 'none',
                      zIndex: 1000,
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                    }}>
                      JTEC Units 1&2 uptime improves from 60% baseline to 90% with 50MW baseload datacenter operation. This provides stable baseload revenue and reduces cycling wear.
                    </div>
                  </div>
                </div>
                <div className="metric-value">+{15}%</div>
                <div className="metric-change">73-78% vs 60% baseline</div>
              </div>
              <div className="metric-card">
                <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                  <div className="metric-label">Total JTEC Benefits</div>
                  <div style={{position: 'relative', display: 'inline-block'}}>
                    <span style={{color: '#3b82f6', cursor: 'help', fontSize: '0.875rem'}}>ℹ️</span>
                    <div className="tooltip" style={{
                      position: 'absolute',
                      bottom: '20px',
                      left: '-100px',
                      width: '250px',
                      padding: '0.5rem',
                      background: 'rgba(30, 41, 59, 0.95)',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      borderRadius: '6px',
                      fontSize: '0.7rem',
                      color: '#e2e8f0',
                      display: 'none',
                      zIndex: 1000,
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                    }}>
                      Total annual economic benefits: Cold starts avoided ($0.4M) + Reduced cycling ($0.5M) + Heat rate improvements ($2.2M)
                    </div>
                  </div>
                </div>
                <div className="metric-value">${(3.1).toFixed(1)}M</div>
                <div className="metric-change">$2.8-3.6M range/year</div>
              </div>
            </div>
          </div>
          
          {/* Optional Market Curtail-for-Sale Section */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(99, 102, 241, 0.05) 100%)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            borderRadius: '12px',
            padding: '1.5rem',
            marginTop: '2rem'
          }}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
              <h3 style={{color: '#10b981', margin: 0}}>
                OPTIONAL MARKET CURTAIL-FOR-SALE: {monthAbbreviations[(monthWindow % 12) + 1]} {2014 + Math.floor(monthWindow/12)} - {monthAbbreviations[((monthWindow + 60) % 12) + 1]} {2014 + Math.floor((monthWindow + 60)/12)} Window
              </h3>
              <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                <span style={{color: '#94a3b8', fontSize: '0.875rem'}}>Revenue Split (CU/Op):</span>
                <select
                  value={revenueSplit}
                  onChange={(e) => setRevenueSplit(e.target.value)}
                  style={{
                    background: 'rgba(30, 41, 59, 0.9)',
                    border: '1px solid rgba(148, 163, 184, 0.3)',
                    borderRadius: '4px',
                    color: '#e2e8f0',
                    padding: '0.25rem 0.5rem',
                    fontSize: '0.875rem'
                  }}
                >
                  <option value="0/100">0/100</option>
                  <option value="20/80">20/80</option>
                  <option value="25/75">25/75</option>
                  <option value="40/60">40/60</option>
                  <option value="50/50">50/50</option>
                  <option value="60/40">60/40</option>
                  <option value="75/25">75/25</option>
                  <option value="80/20">80/20</option>
                  <option value="100/0">100/0</option>
                </select>
              </div>
            </div>
            <div className="metrics-grid">
              <div className="metric-card">
                <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                  <div className="metric-label">Curtail-For-Sale Revenue</div>
                  <div style={{position: 'relative', display: 'inline-block'}}>
                    <span style={{color: '#3b82f6', cursor: 'help', fontSize: '0.875rem'}}>ℹ️</span>
                    <div className="tooltip" style={{
                      position: 'absolute',
                      bottom: '20px',
                      left: '-100px',
                      width: '200px',
                      padding: '0.5rem',
                      background: 'rgba(30, 41, 59, 0.95)',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      borderRadius: '6px',
                      fontSize: '0.7rem',
                      color: '#e2e8f0',
                      display: 'none',
                      zIndex: 1000,
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                    }}>
                      Total revenue from selling 50MW to grid: Σ(Market Price - ${curtailmentThreshold}/MWh) × 50MW × hours when DALMP ≥ ${gridSaleThreshold}/MWh.
                    </div>
                  </div>
                </div>
                <div className="metric-value">${(data.stats.totalCurtailmentRevenue / 5 / 1000000).toFixed(1)}M/yr</div>
                <div className="metric-change">${(data.stats.totalCurtailmentRevenue / 1000000).toFixed(1)}M total</div>
              </div>
              <div className="metric-card">
                <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                  <div className="metric-label">CU Revenue Share</div>
                  <div style={{position: 'relative', display: 'inline-block'}}>
                    <span style={{color: '#3b82f6', cursor: 'help', fontSize: '0.875rem'}}>ℹ️</span>
                    <div className="tooltip" style={{
                      position: 'absolute',
                      bottom: '20px',
                      left: '-100px',
                      width: '200px',
                      padding: '0.5rem',
                      background: 'rgba(30, 41, 59, 0.95)',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      borderRadius: '6px',
                      fontSize: '0.7rem',
                      color: '#e2e8f0',
                      display: 'none',
                      zIndex: 1000,
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                    }}>
                      City Utilities' share ({revenueSplit.split('/')[0]}%) of curtailment revenue. Operator gets {revenueSplit.split('/')[1]}%.
                    </div>
                  </div>
                </div>
                <div className="metric-value">${(data.stats.totalCurtailmentRevenue * parseInt(revenueSplit.split('/')[0]) / 100 / 5 / 1000000).toFixed(1)}M/yr</div>
                <div className="metric-change">${(data.stats.totalCurtailmentRevenue * parseInt(revenueSplit.split('/')[0]) / 100 / 1000000).toFixed(1)}M total</div>
              </div>
              <div className="metric-card">
                <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                  <div className="metric-label">Operator Revenue Share</div>
                  <div style={{position: 'relative', display: 'inline-block'}}>
                    <span style={{color: '#3b82f6', cursor: 'help', fontSize: '0.875rem'}}>ℹ️</span>
                    <div className="tooltip" style={{
                      position: 'absolute',
                      bottom: '20px',
                      left: '-100px',
                      width: '200px',
                      padding: '0.5rem',
                      background: 'rgba(30, 41, 59, 0.95)',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      borderRadius: '6px',
                      fontSize: '0.7rem',
                      color: '#e2e8f0',
                      display: 'none',
                      zIndex: 1000,
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                    }}>
                      Operator's share of grid sale revenue based on ${revenueSplit} split, plus flat-rate datacenter revenue.
                    </div>
                  </div>
                </div>
                <div className="metric-value">${(
                  // Annual curtailment revenue share only - Operator gets second number
                  (data.stats.totalCurtailmentRevenue || 0) * (parseInt(revenueSplit.split('/')[1]) / 100) / 5 / 1000000
                ).toFixed(1)}M/yr</div>
                <div className="metric-change">+${(
                  // Annual datacenter revenue
                  (data.stats.datacenterHours + data.stats.normalOperatingHours) * 50 * curtailmentThreshold / 5 / 1000000
                ).toFixed(1)}M datacenter</div>
              </div>
            </div>
          </div>
          
          {/* Modeling Calculations Section - Hidden with small debug icon */}
          <div style={{
            marginTop: '2rem',
            position: 'relative',
            minHeight: '30px'
          }}>
            {/* Debug Icon - Always Visible at bottom */}
            <div 
              onClick={() => setShowModelingCalcs(!showModelingCalcs)}
              style={{
                position: 'absolute',
                bottom: showModelingCalcs ? 'auto' : '0',
                top: showModelingCalcs ? '10px' : 'auto',
                right: '10px',
                cursor: 'pointer',
                opacity: 0.3,
                transition: 'opacity 0.2s',
                fontSize: '0.75rem',
                color: '#64748b',
                zIndex: 10
              }}
              onMouseEnter={(e) => e.target.style.opacity = '0.7'}
              onMouseLeave={(e) => e.target.style.opacity = '0.3'}
              title="Toggle Modeling Calculations"
            >
              {showModelingCalcs ? '📊' : '🔧'}
            </div>
            
            {/* Collapsible Content */}
            {showModelingCalcs && (
              <div style={{
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(99, 102, 241, 0.05) 100%)',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                borderRadius: '12px',
                padding: '1.5rem',
                marginBottom: '2rem'
              }}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
                  <div>
                    <h3 style={{color: '#3b82f6', margin: '0 0 0.5rem 0'}}>
                      MODELING CALCULATIONS
                    </h3>
                    <p style={{color: '#94a3b8', fontSize: '0.75rem', margin: 0}}>
                      {useAllData ? 'Using all 10 years of data' : `Using 5-year window: ${monthAbbreviations[monthWindow % 12 + 1] || 'JAN'} ${2014 + Math.floor(monthWindow/12)} - ${monthAbbreviations[(monthWindow + 59) % 12 + 1] || 'DEC'} ${2014 + Math.floor((monthWindow + 59)/12)}`}
                    </p>
                  </div>
                  <div style={{display: 'flex', gap: '0.5rem'}}>
                    <button
                      onClick={() => setUseAllData(!useAllData)}
                      style={{
                        padding: '0.5rem 1rem',
                        background: useAllData ? 'rgba(239, 68, 68, 0.2)' : 'rgba(59, 130, 246, 0.2)',
                        border: `1px solid ${useAllData ? '#ef4444' : '#3b82f6'}`,
                        color: useAllData ? '#ef4444' : '#3b82f6',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                        fontWeight: 'bold'
                      }}
                    >
                      {useAllData ? 'Use Window Data' : 'Use All 10 Years'}
                    </button>
                    <button
                      onClick={() => {
                        console.log('Manual calculation triggered');
                        console.log('Raw data available:', rawData?.length, 'days');
                        if (rawData && rawData.length > 0) {
                          const testStats = calculateDataStats(rawData.slice(0, 100));
                          console.log('Test stats result:', testStats);
                        }
                      }}
                      style={{
                        padding: '0.5rem 1rem',
                        background: 'rgba(34, 197, 94, 0.2)',
                        border: '1px solid #10b981',
                        color: '#10b981',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                        fontWeight: 'bold'
                      }}
                    >
                      Test Calc
                    </button>
                  </div>
                </div>
                
                <div className="metrics-grid">
                  <div className="metric-card">
                    <div className="metric-label">Price Distribution</div>
                    <div className="metric-value" style={{fontSize: '0.875rem'}}>
                      {dataStats ? `$${dataStats.basic.mean}` : 'Calculating...'}
                    </div>
                    <div className="metric-change" style={{fontSize: '0.7rem', color: '#64748b'}}>
                      {dataStats ? `σ=$${dataStats.basic.stdDev}, Med=$${dataStats.basic.median}` : 'Mean, median, σ, percentiles'}
                    </div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-label">Price Range</div>
                    <div className="metric-value" style={{fontSize: '0.875rem'}}>
                      {dataStats ? `$${dataStats.basic.min}-${dataStats.basic.max}` : 'Calculating...'}
                    </div>
                    <div className="metric-change" style={{fontSize: '0.7rem', color: '#64748b'}}>
                      {dataStats ? `P5=$${dataStats.percentiles.p5}, P95=$${dataStats.percentiles.p95}` : 'Min to Max'}
                    </div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-label">Operating Windows</div>
                    <div className="metric-value" style={{fontSize: '0.875rem'}}>
                      {dataStats ? `${dataStats.capacityFactors.cf_22}% CF` : 'Calculating...'}
                    </div>
                    <div className="metric-change" style={{fontSize: '0.7rem', color: '#64748b'}}>
                      {dataStats ? `@$22/MWh (${dataStats.hoursBelow.below_22} hrs <$22)` : 'Hours below thresholds'}
                    </div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-label">Risk Metrics</div>
                    <div className="metric-value" style={{fontSize: '0.875rem'}}>
                      {dataStats ? `${dataStats.priceBands.negative} neg hrs` : 'Calculating...'}
                    </div>
                    <div className="metric-change" style={{fontSize: '0.7rem', color: '#64748b'}}>
                      {dataStats ? `${dataStats.hoursBelow.below_100 ? (dataStats.metadata.totalHours - dataStats.hoursBelow.below_100) : 0} hrs >$100` : 'Volatility, drawdowns'}
                    </div>
                  </div>
                </div>
                
                {/* Calculation Output Area */}
                <div style={{
                  marginTop: '1rem',
                  padding: '1rem',
                  background: 'rgba(30, 41, 59, 0.3)',
                  borderRadius: '8px',
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  color: '#cbd5e1',
                  maxHeight: '400px',
                  overflowY: 'auto'
                }}>
                  <div style={{marginBottom: '1rem', color: '#94a3b8'}}>
                    <strong>{dataStats ? 'Calculated Statistics (Copy for Research):' : 'Waiting for data...'}</strong>
                  </div>
                  {dataStats ? (
                    <pre style={{whiteSpace: 'pre-wrap', color: '#e2e8f0'}}>
{`# DALMP Historical Data Analysis Results
# Location: JTEC (John Twitty Energy Center)
# Dataset: ${useAllData ? '10-Year Full Dataset' : `5-Year Window: ${monthAbbreviations[monthWindow % 12 + 1] || 'JAN'} ${2014 + Math.floor(monthWindow/12)} - ${monthAbbreviations[(monthWindow + 59) % 12 + 1] || 'DEC'} ${2014 + Math.floor((monthWindow + 59)/12)}`}
# Date Range: ${dataStats.metadata.startDate} to ${dataStats.metadata.endDate}
# Total Hours: ${dataStats.metadata.totalHours.toLocaleString()}
# Total Days: ${dataStats.metadata.totalDays}

## 1. PRICE DISTRIBUTION ANALYSIS
### Basic Statistics
- Mean price: $${dataStats.basic.mean}/MWh
- Median price: $${dataStats.basic.median}/MWh
- Mode price: $${dataStats.basic.mode}/MWh
- Standard deviation: $${dataStats.basic.stdDev}/MWh
- Variance: ${dataStats.basic.variance}
- Minimum price: $${dataStats.basic.min}/MWh
- Maximum price: $${dataStats.basic.max}/MWh
- Skewness: ${dataStats.basic.skewness}
- Kurtosis: ${dataStats.basic.kurtosis}

### Percentile Distribution
- 1st percentile: $${dataStats.percentiles.p1}/MWh
- 5th percentile: $${dataStats.percentiles.p5}/MWh
- 10th percentile: $${dataStats.percentiles.p10}/MWh
- 25th percentile (Q1): $${dataStats.percentiles.p25}/MWh
- 50th percentile (Median): $${dataStats.percentiles.p50}/MWh
- 75th percentile (Q3): $${dataStats.percentiles.p75}/MWh
- 90th percentile: $${dataStats.percentiles.p90}/MWh
- 95th percentile: $${dataStats.percentiles.p95}/MWh
- 99th percentile: $${dataStats.percentiles.p99}/MWh

### Price Band Distribution (hours per ${useAllData ? '10 years' : '5 years'})
- Below $0 (negative): ${dataStats.priceBands.negative} hours (${(dataStats.priceBands.negative/dataStats.metadata.totalHours*100).toFixed(2)}%)
- $0-10/MWh: ${dataStats.priceBands.band_0_10} hours (${(dataStats.priceBands.band_0_10/dataStats.metadata.totalHours*100).toFixed(2)}%)
- $10-20/MWh: ${dataStats.priceBands.band_10_20} hours (${(dataStats.priceBands.band_10_20/dataStats.metadata.totalHours*100).toFixed(2)}%)
- $20-30/MWh: ${dataStats.priceBands.band_20_30} hours (${(dataStats.priceBands.band_20_30/dataStats.metadata.totalHours*100).toFixed(2)}%)
- $30-40/MWh: ${dataStats.priceBands.band_30_40} hours (${(dataStats.priceBands.band_30_40/dataStats.metadata.totalHours*100).toFixed(2)}%)
- $40-50/MWh: ${dataStats.priceBands.band_40_50} hours (${(dataStats.priceBands.band_40_50/dataStats.metadata.totalHours*100).toFixed(2)}%)
- $50-75/MWh: ${dataStats.priceBands.band_50_75} hours (${(dataStats.priceBands.band_50_75/dataStats.metadata.totalHours*100).toFixed(2)}%)
- $75-100/MWh: ${dataStats.priceBands.band_75_100} hours (${(dataStats.priceBands.band_75_100/dataStats.metadata.totalHours*100).toFixed(2)}%)
- $100-200/MWh: ${dataStats.priceBands.band_100_200} hours (${(dataStats.priceBands.band_100_200/dataStats.metadata.totalHours*100).toFixed(2)}%)
- Above $200/MWh: ${dataStats.priceBands.band_200_plus} hours (${(dataStats.priceBands.band_200_plus/dataStats.metadata.totalHours*100).toFixed(2)}%)

## 2. TEMPORAL PATTERNS
### Hour of Day Analysis (00:00-23:00)
${dataStats.hourlyAverages.map((avg, idx) => `- Hour ${idx.toString().padStart(2, '0')}: $${avg}/MWh`).join('\n')}

### Day of Week Analysis
- Sunday: $${dataStats.dayOfWeekAverages.sunday}/MWh
- Monday: $${dataStats.dayOfWeekAverages.monday}/MWh
- Tuesday: $${dataStats.dayOfWeekAverages.tuesday}/MWh
- Wednesday: $${dataStats.dayOfWeekAverages.wednesday}/MWh
- Thursday: $${dataStats.dayOfWeekAverages.thursday}/MWh
- Friday: $${dataStats.dayOfWeekAverages.friday}/MWh
- Saturday: $${dataStats.dayOfWeekAverages.saturday}/MWh
- Weekday avg: $${((parseFloat(dataStats.dayOfWeekAverages.monday) + parseFloat(dataStats.dayOfWeekAverages.tuesday) + parseFloat(dataStats.dayOfWeekAverages.wednesday) + parseFloat(dataStats.dayOfWeekAverages.thursday) + parseFloat(dataStats.dayOfWeekAverages.friday))/5).toFixed(2)}/MWh
- Weekend avg: $${((parseFloat(dataStats.dayOfWeekAverages.saturday) + parseFloat(dataStats.dayOfWeekAverages.sunday))/2).toFixed(2)}/MWh

### Monthly Seasonality
${['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((month, idx) => `- ${month}: $${dataStats.monthlyAverages[idx]}/MWh`).join('\n')}
- Summer (Jun-Aug) avg: $${((parseFloat(dataStats.monthlyAverages[5]) + parseFloat(dataStats.monthlyAverages[6]) + parseFloat(dataStats.monthlyAverages[7]))/3).toFixed(2)}/MWh
- Winter (Dec-Feb) avg: $${((parseFloat(dataStats.monthlyAverages[11]) + parseFloat(dataStats.monthlyAverages[0]) + parseFloat(dataStats.monthlyAverages[1]))/3).toFixed(2)}/MWh
- Shoulder months avg: $${((parseFloat(dataStats.monthlyAverages[2]) + parseFloat(dataStats.monthlyAverages[3]) + parseFloat(dataStats.monthlyAverages[4]) + parseFloat(dataStats.monthlyAverages[8]) + parseFloat(dataStats.monthlyAverages[9]) + parseFloat(dataStats.monthlyAverages[10]))/6).toFixed(2)}/MWh

## 3. MINING PROFITABILITY THRESHOLDS
### Hours Below Threshold
- Hours < $0/MWh: ${dataStats.hoursBelow.below_0} (${(dataStats.hoursBelow.below_0/dataStats.metadata.totalHours*100).toFixed(2)}%)
- Hours < $10/MWh: ${dataStats.hoursBelow.below_10} (${(dataStats.hoursBelow.below_10/dataStats.metadata.totalHours*100).toFixed(2)}%)
- Hours < $22/MWh (CPER): ${dataStats.hoursBelow.below_22} (${(dataStats.hoursBelow.below_22/dataStats.metadata.totalHours*100).toFixed(2)}%)
- Hours < $30/MWh: ${dataStats.hoursBelow.below_30} (${(dataStats.hoursBelow.below_30/dataStats.metadata.totalHours*100).toFixed(2)}%)
- Hours < $40/MWh: ${dataStats.hoursBelow.below_40} (${(dataStats.hoursBelow.below_40/dataStats.metadata.totalHours*100).toFixed(2)}%)
- Hours < $50/MWh: ${dataStats.hoursBelow.below_50} (${(dataStats.hoursBelow.below_50/dataStats.metadata.totalHours*100).toFixed(2)}%)
- Hours < $65/MWh: ${dataStats.hoursBelow.below_65} (${(dataStats.hoursBelow.below_65/dataStats.metadata.totalHours*100).toFixed(2)}%)
- Hours < $75/MWh: ${dataStats.hoursBelow.below_75} (${(dataStats.hoursBelow.below_75/dataStats.metadata.totalHours*100).toFixed(2)}%)
- Hours < $100/MWh: ${dataStats.hoursBelow.below_100} (${(dataStats.hoursBelow.below_100/dataStats.metadata.totalHours*100).toFixed(2)}%)

## 4. CAPACITY FACTORS AT CURTAILMENT THRESHOLDS
- CF if curtail > $20/MWh: ${dataStats.capacityFactors.cf_20}%
- CF if curtail > $22/MWh: ${dataStats.capacityFactors.cf_22}%
- CF if curtail > $30/MWh: ${dataStats.capacityFactors.cf_30}%
- CF if curtail > $40/MWh: ${dataStats.capacityFactors.cf_40}%
- CF if curtail > $50/MWh: ${dataStats.capacityFactors.cf_50}%
- CF if curtail > $75/MWh: ${dataStats.capacityFactors.cf_75}%
- CF if curtail > $100/MWh: ${dataStats.capacityFactors.cf_100}%

## 5. VOLATILITY AND RISK METRICS
### Price Volatility
- Average hourly change: $${dataStats.volatility.avgHourlyChange}/MWh
- Maximum hourly increase: $${dataStats.volatility.maxHourlyIncrease}/MWh
- Maximum hourly decrease: $${dataStats.volatility.maxHourlyDecrease}/MWh

## JSON FORMAT OUTPUT
{
  "metadata": {
    "startDate": "${dataStats.metadata.startDate}",
    "endDate": "${dataStats.metadata.endDate}",
    "totalHours": ${dataStats.metadata.totalHours},
    "totalDays": ${dataStats.metadata.totalDays},
    "location": "JTEC"
  },
  "priceStats": ${JSON.stringify(dataStats.basic, null, 2)},
  "percentiles": ${JSON.stringify(dataStats.percentiles, null, 2)},
  "priceBands": ${JSON.stringify(dataStats.priceBands, null, 2)},
  "thresholdAnalysis": ${JSON.stringify(dataStats.hoursBelow, null, 2)},
  "capacityFactors": ${JSON.stringify(dataStats.capacityFactors, null, 2)},
  "temporalPatterns": {
    "hourly": ${JSON.stringify(dataStats.hourlyAverages, null, 2)},
    "dayOfWeek": ${JSON.stringify(dataStats.dayOfWeekAverages, null, 2)},
    "monthly": ${JSON.stringify(dataStats.monthlyAverages, null, 2)}
  },
  "volatilityMetrics": ${JSON.stringify(dataStats.volatility, null, 2)}
}`}
                    </pre>
                  ) : (
                    <pre style={{whiteSpace: 'pre-wrap', color: '#e2e8f0'}}>
{`# DALMP Historical Data Analysis Calculations
# For 10 years of hourly DALMP data at JTEC location

## 1. PRICE DISTRIBUTION ANALYSIS
### Basic Statistics
- Mean price ($/MWh)
- Median price ($/MWh)
- Mode price ($/MWh)
- Standard deviation
- Minimum price
- Maximum price
- Skewness
- Kurtosis

### Percentile Distribution
- 1st percentile
- 5th percentile
- 10th percentile
- 25th percentile (Q1)
- 50th percentile (Median)
- 75th percentile (Q3)
- 90th percentile
- 95th percentile
- 99th percentile

### Price Band Distribution (hours per year average)
- Below $0 (negative prices)
- $0-10/MWh
- $10-20/MWh
- $20-30/MWh
- $30-40/MWh
- $40-50/MWh
- $50-75/MWh
- $75-100/MWh
- $100-200/MWh
- Above $200/MWh

## 2. TEMPORAL PATTERNS
### Hour of Day Analysis (00:00-23:00)
- Average price by hour
- Std deviation by hour
- 90th percentile by hour
- 10th percentile by hour

### Day of Week Analysis
- Monday average
- Tuesday average
- Wednesday average
- Thursday average
- Friday average
- Saturday average
- Sunday average
- Weekday vs Weekend differential

### Monthly Seasonality
- January average
- February average
- March average
- April average
- May average
- June average
- July average
- August average
- September average
- October average
- November average
- December average
- Summer (Jun-Aug) average
- Winter (Dec-Feb) average
- Shoulder months average

### Year-over-Year Trends
- Annual average by year
- Annual volatility by year
- Negative price hours by year
- High price (>$100) hours by year

## 3. MINING PROFITABILITY THRESHOLDS
### Hours Below Threshold (annual average)
- Hours < $0/MWh
- Hours < $10/MWh
- Hours < $22/MWh (CPER rate)
- Hours < $30/MWh
- Hours < $40/MWh
- Hours < $50/MWh (typical breakeven)
- Hours < $65/MWh (recommended flat rate)
- Hours < $75/MWh
- Hours < $100/MWh

### Consecutive Hours Analysis
- Maximum consecutive hours < $22/MWh
- Average consecutive hours < $22/MWh
- Maximum consecutive hours < $50/MWh
- Average consecutive hours < $50/MWh
- Maximum consecutive hours > $100/MWh
- Average consecutive hours > $100/MWh

### Operating Window Statistics
- Longest profitable run at $22/MWh threshold
- Average profitable run duration
- Number of starts/stops per year at $22/MWh
- Number of starts/stops per year at $50/MWh

## 4. VOLATILITY AND RISK METRICS
### Price Volatility
- Hourly volatility (hour-to-hour changes)
- Daily volatility (daily average std dev)
- Monthly volatility
- Annual volatility

### Price Swings
- Average daily range (high - low)
- Maximum daily range
- 95th percentile daily range
- Average hourly change (absolute)
- Maximum hourly increase
- Maximum hourly decrease

### Extreme Events
- Frequency of negative prices (hours/year)
- Average duration of negative price events
- Frequency of prices > $100 (hours/year)
- Average duration of high price events
- Frequency of prices > $200 (hours/year)
- Frequency of prices > $500 (hours/year)

## 5. REVENUE OPTIMIZATION ANALYSIS
### Capacity Factor at Different Thresholds
- CF if curtail > $20/MWh: ____%
- CF if curtail > $22/MWh: ____%
- CF if curtail > $30/MWh: ____%
- CF if curtail > $40/MWh: ____%
- CF if curtail > $50/MWh: ____%
- CF if curtail > $75/MWh: ____%
- CF if curtail > $100/MWh: ____%

### Annual Revenue Impact (50MW @ various strategies)
- Revenue if always run (no curtailment)
- Revenue if curtail > $22/MWh
- Revenue if curtail > $50/MWh
- Revenue if curtail > $75/MWh
- Optimal threshold (maximizes datacenter profit - power cost)

### Curtailment Cost Analysis
- Avg opportunity cost per curtailed hour
- Total annual opportunity cost at $22 threshold
- Total annual opportunity cost at $50 threshold
- Break-even BTC price for each threshold

## 6. RISK SCENARIO ANALYSIS
### Worst Case Scenarios
- Worst consecutive 24 hours (average price)
- Worst consecutive 7 days (average price)
- Worst consecutive 30 days (average price)
- Worst year (average price)

### Best Case Scenarios
- Best consecutive 24 hours (average price)
- Best consecutive 7 days (average price)
- Best consecutive 30 days (average price)
- Best year (average price)

### Value at Risk (VaR)
- 95% VaR (daily)
- 99% VaR (daily)
- 95% VaR (monthly)
- 99% VaR (monthly)

## 7. CORRELATION ANALYSIS
### Autocorrelation
- Hour t to t+1 correlation
- Hour t to t+24 correlation
- Day to day correlation
- Week to week correlation

### External Factors (if data available)
- Correlation with temperature
- Correlation with natural gas prices
- Correlation with wind generation
- Correlation with solar generation

## 8. GRID STABILITY INDICATORS
### System Stress Periods
- Hours with negative prices (oversupply)
- Hours > $200/MWh (scarcity)
- Frequency of rapid price changes (>$50/hr)
- Average ramp rates

### Congestion Patterns
- Hours with location-specific premiums
- Average congestion cost
- Seasonal congestion patterns

## OUTPUT FORMAT
Please provide results in JSON format:
{
  "metadata": {
    "startDate": "YYYY-MM-DD",
    "endDate": "YYYY-MM-DD",
    "totalHours": NUMBER,
    "location": "JTEC"
  },
  "priceStats": { ... },
  "temporalPatterns": { ... },
  "thresholdAnalysis": { ... },
  "volatilityMetrics": { ... },
  "revenueOptimization": { ... },
  "riskScenarios": { ... },
  "correlations": { ... },
  "gridIndicators": { ... }
}`}
                  </pre>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
    </div>
  );
}

export default EnergyCalculator;